import { useMutation, useQuery, useSubscription } from "@apollo/client";
import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import { GET_COMMANDS, GET_INITIAL_COMMANDS, GET_UPCOMING_FIXTURES } from "../../graphql/queries";
import { COMMAND_SUBSCRIPTION, INITIAL_SUBSCRIPTION } from "../../graphql/subscription";
import { RESET_RENDERER, UPDATE_RENDERER } from "../../graphql/mutation";
import { useNavigate, useParams } from "react-router-dom";
import {
  MaestroProvider,
  MaestroHeaderView,
  MaestroCaptionNavView,
  MaestroCaptionInputsView,
  MaestroActionButtonsView,
  MaestroFormView,
  MaestroJsonViewerView,
  MaestroPreviewView,
  createBindData,
} from '@ae/maestrov2-form-lib';
import { useFixtureContext } from '../../hooks/useFixtureContext';
import { getDDSClient } from '../../lib/ddsClient';
import AetherClient from '../../aetherApolloClient';
import {
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  ToggleButtonGroup,
  ToggleButton as ToggleButtonMUI,
  Box,
  Grid,
  Button,
  Paper,
  IconButton,
  Divider,
  TextField,
  MenuItem,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Chip
} from "@mui/material";
import SceneViewer from "../../components/RendererViewer";
import JsonForm from "./JsonForm";
import { ChevronLeftRounded, ChevronRightRounded, ExpandMoreRounded, PictureInPictureAlt } from "@mui/icons-material";
import { useSession } from '../../contexts/SessionContext';
import MergedFixturePicker from "../../components/Renderer/MergedFixturePicker";
import { createDataServices } from "../../services/data/dataServiceFactory";
import { useAlert } from "../../contexts/AlertContext";
import RestartAlt from '@mui/icons-material/RestartAlt';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FastForwardIcon from '@mui/icons-material/FastForward';
import GPIControls from "../../components/Renderer/GPIControl";
import PluginRenderer from "../../components/PluginRenderer";
import apiService from "../../services/api.service";
import PresenceStack from "../../components/presence/PresenceStack";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { Icon as OpusIcon, Badge as OpusBadge, StateShell, Toggle as OpusToggle, Button as OpusButton, Modal as OpusModal } from "../../components/common/opus";
import { AuthContext } from "../../contexts/AuthContext";
import { getAvatarPref } from "../../utils/avatarPref";
import { groupByLayer, normalizeLayer } from "../../services/scenes/captionPayload";

// Module-scope bindData wrapper — same pattern AeDrive uses (see
// BB_AeDrive_Web/aedrive-ui/src/pages/TXLive/TXLive.jsx:100). One
// instance per page so all <MaestroProvider> mounts share its AST
// cache + skeleton + memo + in-flight coalescer. Created once,
// reused for every caption / fixture switch.
const bindData = createBindData();

const BROKER_BASE_URL = process.env.REACT_APP_BROKER_API_URL || 'http://localhost:8010';

// OPUS-1045: [0] is 0.23 to match the first pane's CSS minWidth (23%). They were
// out of sync (JS clamp 0.18 vs CSS 23%), so dragging pane 0 below 23% let the
// neighbour grow while CSS pinned pane 0 — the column resize glitched. Aligned.
const MIN_WIDTHS = [0.23, 0.28, 0.30];
const MAX_WIDTHS = [0.45, 0.62, 0.5];


const SceneControls = () => {
  const containerRef = useRef(null);
  const [widths, setWidths] = useState([0.21, 0.43, 0.36]);
  const [isDragging, setIsDragging] = useState(null);
  const formRef = useRef();
  const scrollRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const { isDrawerOpen, toggleDrawer, isCaptionActionListOpen, toggleCaptionActionList, isCaptionDataOpen, toggleCaptionData, isClockControlOpen, toggleClockControl } = useSession();
  const [payload, setPayload] = useState({});
  const [brokerFormData, setBrokerFormData] = useState({});
  const [commandsLoadedFor, setCommandsLoadedFor] = useState(null);
  const navigate = useNavigate();
  const { userInfo } = useContext(AuthContext);
  const currentUserId = userInfo?.data?._id;
  const { rendererId } = useParams('rendererId');

  // OPUS-913: live presence — register this user as a viewer of this renderer
  // (heartbeat every 12s; leave on unmount). Backend evicts stale viewers ~30s,
  // so a closed tab self-clears. Best-effort: never blocks the page.
  useEffect(() => {
    if (!rendererId) return undefined;
    let cancelled = false;
    const beat = () => { if (!cancelled) apiService.rendererHeartbeat(rendererId, getAvatarPref(currentUserId)).catch(() => {}); };
    beat();
    const iv = setInterval(beat, 12000);
    return () => {
      cancelled = true;
      clearInterval(iv);
      apiService.rendererLeave(rendererId).catch(() => {});
    };
  }, [rendererId, currentUserId]);

  // OPUS-913: poll who else is currently viewing this renderer (read-only) so
  // the breadcrumb header can show the live presence cluster. Best-effort.
  const [viewers, setViewers] = useState([]);
  useEffect(() => {
    if (!rendererId) return undefined;
    let cancelled = false;
    const load = () => apiService.getRendererPresence(rendererId)
      .then((resp) => {
        if (cancelled) return;
        const list = resp?.data?.viewers || resp?.viewers || resp?.data || resp || [];
        setViewers(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [rendererId]);

  const [captionList, setCaptionList] = useState([]);

  // OPUS-1391: templates bucketed by player layer. Rows keep their index into
  // captionList because selectedCaption addresses them by position.
  //
  // Unlike the scene editor, empty layers are dropped and the headers only show
  // when more than one layer is actually in use — an operator cannot assign
  // layers from here, so a lone "MAIN" header would be pure noise on the scenes
  // that have not been assigned yet.
  const captionLayerGroups = useMemo(() => {
    const groups = groupByLayer(captionList).filter((g) => g.rows.length > 0);
    return { groups, showHeaders: groups.length > 1 };
  }, [captionList]);
  const [selectedCaption, setSelectedCaption] = useState(null);
  // Bumping this forces a MaestroProvider remount. Used for the "reselect
  // the same template" affordance (a scene with one template has no other
  // row to bounce off of) and for auto-rebinding when the active fixture
  // changes — both FIFA Connect Fixture and PLS on-pick flow through
  // selectedFixtureId, so a single effect below covers both.
  const [captionReloadKey, setCaptionReloadKey] = useState(0);
  // Live broker truth for THIS renderer's data connector. Seeded from
  // getCommands on load, updated on every pubSub.publish('COMMAND', …)
  // that includes a DataConnector field. Shape: { rendererId, fixtureId,
  // enabled, databaseName, updatedAt } | null.
  const [liveConnector, setLiveConnector] = useState(null);
  const [renderer, setRenderer] = useState(rendererId);
  const [captionAction, setCaptionAction] = useState({});
  const [scenePath, setScenePath] = useState(null);
  const [sharedState, setSharedState] = useState(null);
  const [clockData, setClockData] = useState([]);
  // Collapsed by default — DATA SOURCE moved to the top of the right
  // pane (2026-05-14) but should not push CAPTION ACTIONS / CAPTION
  // PROPERTIES below the fold on initial load. Operator expands when
  // they need to pick a fixture.
  const [isDataSourceOpen, setIsDataSourceOpenRaw] = useState(() => {
    try { return localStorage.getItem('dashboard:accordion:dataSource') === 'true'; }
    catch { return false; }
  });
  const setIsDataSourceOpen = React.useCallback((open) => {
    setIsDataSourceOpenRaw(open);
    try { localStorage.setItem('dashboard:accordion:dataSource', String(open)); } catch { /* ignore */ }
  }, []);
  const [isResetting, setIsResetting] = useState(false);
  const [rendererUrlCopied, setRendererUrlCopied] = useState(false);
  const [captionData, setCaptionData] = useState({
    renderer,
    caption: { action: "clear", id: "" },
    updatedBy: "none",
  });
  const [actionList, setActionList] = useState([]);
  const [initialCommadData, setInitialCommadData] = useState({ eventId: '', renderer });
  const [rendererDetails, setRendererDetails] = useState(null);
  const alert = useAlert();
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [captionResetConfirmOpen, setCaptionResetConfirmOpen] = useState(false);
  const [isCaptionResetting, setIsCaptionResetting] = useState(false);
  // OPUS-1124: "Clear all GFX" — animate all on-air graphics out, then reset.
  const [clearGfxConfirmOpen, setClearGfxConfirmOpen] = useState(false);
  const [isClearingGfx, setIsClearingGfx] = useState(false);
  const [captionsWithPlugins, setCaptionsWithPlugins] = useState({});
  const [gpiDataFromQuery, setGpiDataFromQuery] = useState(null);
  const [scenePluginExists, setScenePluginExists] = useState(false);
  const [pluginTitle, setPluginTitle] = useState("PLUGIN CONTROLS");
  const [pluginConfig, setPluginConfig] = useState(null);
  const [assetOptions, setAssetOptions] = useState([]);
  const [organizationName, setOrganizationName] = useState('');
  const [sceneName, setSceneName] = useState('');
  const [dataMode, setDataMode] = useState(true);
  const [captionV2Status, setCaptionV2Status] = useState({});

  // ── Data-source state (FIFA / PLS) ────────────────────────────
  // The operator picks which data source feeds the picker + the
  // form-library's dataService.getContext(). Persisted per-machine
  // because the same operator typically works one source for the
  // duration of a deployment.
  const [dataSource, setDataSource] = useState(() => {
    try { return localStorage.getItem('dashboard:dataSource') || 'fifa'; }
    catch { return 'fifa'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dashboard:dataSource', dataSource); } catch { /* ignore */ }
  }, [dataSource]);

  // The fixtureId the operator picked. Captured from the picker's
  // selection callback; passed to useFixtureContext below so the
  // form-library's dataService.getContext() can resolve against the
  // real fixture state.
  //
  // Persisted PER SOURCE (`dashboard:selectedFixtureId:fifa|pls`) so
  // the operator's last fixture for each source survives reload AND
  // a source toggle doesn't blank the other source's selection. The
  // effect below restores the right one when `dataSource` changes.
  const [selectedFixtureId, setSelectedFixtureIdRaw] = useState(() => {
    try { return localStorage.getItem(`dashboard:selectedFixtureId:${dataSource}`) || null; }
    catch { return null; }
  });
  // Human-readable label for the selected fixture — surfaced in the
  // DATA SOURCE accordion's collapsed summary so the operator can see
  // what's connected without expanding it.
  const [selectedFixtureLabel, setSelectedFixtureLabelRaw] = useState(() => {
    try { return localStorage.getItem(`dashboard:selectedFixtureLabel:${dataSource}`) || null; }
    catch { return null; }
  });
  const setSelectedFixtureId = React.useCallback((id, label) => {
    setSelectedFixtureIdRaw(id);
    setSelectedFixtureLabelRaw(label || null);
    try {
      if (id) {
        localStorage.setItem(`dashboard:selectedFixtureId:${dataSource}`, id);
        if (label) localStorage.setItem(`dashboard:selectedFixtureLabel:${dataSource}`, label);
        else localStorage.removeItem(`dashboard:selectedFixtureLabel:${dataSource}`);
      } else {
        localStorage.removeItem(`dashboard:selectedFixtureId:${dataSource}`);
        localStorage.removeItem(`dashboard:selectedFixtureLabel:${dataSource}`);
      }
    } catch { /* ignore */ }
  }, [dataSource]);
  // Source switch — load that source's last-selected fixture (or null
  // if none was previously picked under that source).
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`dashboard:selectedFixtureId:${dataSource}`);
      setSelectedFixtureIdRaw(stored || null);
      setSelectedFixtureLabelRaw(localStorage.getItem(`dashboard:selectedFixtureLabel:${dataSource}`) || null);
    } catch { setSelectedFixtureIdRaw(null); setSelectedFixtureLabelRaw(null); }
  }, [dataSource]);

  // Auto-rebind the form whenever the active fixture changes. Both
  // FIFA's Connect Fixture flow and PLS's on-pick-no-button flow funnel
  // through setSelectedFixtureId, so watching this single state is
  // enough to cover both. Bumping captionReloadKey forces MaestroProvider
  // to remount with a fresh dataService context → bindings re-resolve
  // against the new fixture without the operator having to reselect the
  // template.
  useEffect(() => {
    if (selectedFixtureId) {
      setCaptionReloadKey((k) => k + 1);
    }
  }, [selectedFixtureId]);

  // FIFA competitions are discovered at runtime from the connected
  // tenant (see the getCompetitions effect below) rather than
  // hardcoded — so the dropdown is correct in dev AND prod with no
  // per-environment code change. `fifaCompetitions` holds the fetched
  // list; `fifaCompetitionId` holds the selection (persisted
  // per-renderer; may be null until the list resolves).
  const [fifaCompetitions, setFifaCompetitions] = useState([]);
  const [fifaCompetitionId, setFifaCompetitionIdRaw] = useState(() => {
    try { return localStorage.getItem('dashboard:fifaCompetitionId') || null; }
    catch { return null; }
  });
  // OPUS-1045: PLS competitions + seasons are discovered from DDS (the dev
  // tenant carries Premier League, J1 League, Premier Division, …) rather than
  // a single hardcoded competition. Defaults seed the previously-hardcoded
  // Premier League / 2025-26 IDs so behaviour is unchanged until discovery
  // resolves and reconciles the selection.
  const [plsCompetitions, setPlsCompetitions] = useState([]);
  const [plsCompetitionId, setPlsCompetitionIdRaw] = useState(() => {
    try { return localStorage.getItem('dashboard:plsCompetitionId') || '2kwbbcootiqqgmrzs6o5inle5'; }
    catch { return '2kwbbcootiqqgmrzs6o5inle5'; }
  });
  const [plsSeasons, setPlsSeasons] = useState([]);
  const [plsSeasonId, setPlsSeasonIdRaw] = useState(() => {
    try { return localStorage.getItem('dashboard:plsSeasonId') || '51r6ph2woavlbbpk8f29nynf8'; }
    catch { return '51r6ph2woavlbbpk8f29nynf8'; }
  });
  // The competition+season the CONNECTED fixture binds against. Kept SEPARATE
  // from the browse selection above so browsing the picker can never disturb a
  // live binding — only an explicit Connect pins these (handleConnectFixture).
  const [plsBoundCompetitionId, setPlsBoundCompetitionId] = useState(() => {
    try { return localStorage.getItem('dashboard:plsBoundCompetitionId') || '2kwbbcootiqqgmrzs6o5inle5'; }
    catch { return '2kwbbcootiqqgmrzs6o5inle5'; }
  });
  const [plsBoundSeasonId, setPlsBoundSeasonId] = useState(() => {
    try { return localStorage.getItem('dashboard:plsBoundSeasonId') || '51r6ph2woavlbbpk8f29nynf8'; }
    catch { return '51r6ph2woavlbbpk8f29nynf8'; }
  });
  // Picked-but-not-yet-connected fixture. The picker sets this on select; the
  // explicit Connect button commits it (OPUS-1125 "Connect for both").
  const [pendingFixture, setPendingFixture] = useState(() =>
    selectedFixtureId ? { id: selectedFixtureId, label: selectedFixtureLabel, source: dataSource } : null,
  );
  // Which commit action is in flight ('connect' | 'disconnect' | null) — drives
  // the two button labels independently so a Switch&Connect never shows "Stopping…".
  const [pendingAction, setPendingAction] = useState(null);
  const connecting = pendingAction === 'connect';
  const disconnecting = pendingAction === 'disconnect';

  const setFifaCompetitionId = React.useCallback((id) => {
    setFifaCompetitionIdRaw(id);
    try { localStorage.setItem('dashboard:fifaCompetitionId', id); } catch { /* ignore */ }
    // OPUS-1125: browsing FIFA competitions must NOT disconnect the bound
    // fixture (it could be a live PLS or FIFA feed). Only drop a not-yet-
    // connected FIFA pending pick — it won't be in the new competition's set.
    setPendingFixture((p) => (p && p.source === 'fifa' ? null : p));
  }, []);

  const setPlsCompetitionId = React.useCallback((id) => {
    setPlsCompetitionIdRaw(id);
    try { localStorage.setItem('dashboard:plsCompetitionId', id); } catch { /* ignore */ }
    // Switching competition invalidates the bound season — the seasons effect
    // reconciles plsSeasonId once DDS returns the new competition's seasons.
    setPendingFixture((p) => (p && p.source === 'pls' ? null : p));
  }, []);
  const setPlsSeasonId = React.useCallback((id) => {
    setPlsSeasonIdRaw(id);
    try { localStorage.setItem('dashboard:plsSeasonId', id); } catch { /* ignore */ }
    setPendingFixture((p) => (p && p.source === 'pls' ? null : p));
  }, []);

  const fifaConfig = React.useMemo(() => ({
    competitionId: fifaCompetitionId,
    databaseName: 'aether-db-fifa',
  }), [fifaCompetitionId]);
  // OPUS-1045: the picker BROWSES this competition+season (read-only listing
  // via plsPickerCatalogue) — changing it must NOT touch a live binding.
  const plsBrowseConfig = React.useMemo(() => ({
    competitionId: plsCompetitionId,
    seasonId: plsSeasonId,
  }), [plsCompetitionId, plsSeasonId]);
  // The CONNECTED fixture binds against the pinned competition+season; Connect
  // copies the browse selection into these, so browsing alone never disturbs a
  // live feed and a reload restores the right context.
  const plsConfig = React.useMemo(() => ({
    competitionId: plsBoundCompetitionId,
    seasonId: plsBoundSeasonId,
  }), [plsBoundCompetitionId, plsBoundSeasonId]);

  // The form-library's dataService comes from this hook. Identity
  // is stable across fixtureId changes, so the MaestroProvider
  // mounted below doesn't tear down its orchestrator on every
  // fixture switch.
  const ddsClient = getDDSClient();
  const { dataService: fixtureDataService } = useFixtureContext({
    source: dataSource,
    fixtureId: selectedFixtureId,
    apolloClient: AetherClient,
    ddsClient,
    fifaConfig,
    plsConfig,
  });

  // OPUS-1125: the unified picker pulls from BOTH sources at once, so build
  // a catalogue per source independently of whichever source is currently
  // bound to the form. Catalogues are lightweight query wrappers.
  const fifaPickerCatalogue = React.useMemo(
    () => createDataServices({ source: 'fifa', apolloClient: AetherClient, ddsClient, fifaConfig, plsConfig }).catalogue,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ddsClient, fifaConfig, plsConfig],
  );
  const plsPickerCatalogue = React.useMemo(
    () => createDataServices({ source: 'pls', apolloClient: AetherClient, ddsClient, fifaConfig, plsConfig: plsBrowseConfig }).catalogue,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ddsClient, fifaConfig, plsBrowseConfig],
  );

  // Discover FIFA competitions for the picker's Competition dropdown,
  // independent of which source is currently bound. Reconcile the persisted
  // selection: keep it if still valid, otherwise fall back to the first.
  useEffect(() => {
    if (!fifaPickerCatalogue || typeof fifaPickerCatalogue.getCompetitions !== 'function') {
      return undefined;
    }
    let cancelled = false;
    fifaPickerCatalogue.getCompetitions()
      .then((comps) => {
        if (cancelled || !Array.isArray(comps)) return;
        setFifaCompetitions(comps);
        setFifaCompetitionIdRaw((prev) => {
          if (prev && comps.some((c) => c.id === prev)) return prev;
          const next = comps[0]?.id || null;
          try { if (next) localStorage.setItem('dashboard:fifaCompetitionId', next); } catch { /* ignore */ }
          return next;
        });
      })
      .catch(() => { if (!cancelled) setFifaCompetitions([]); });
    return () => { cancelled = true; };
  }, [fifaPickerCatalogue]);

  // OPUS-1045: discover PLS competitions (DDS) for the Competition dropdown,
  // reconciling the persisted selection the same way as FIFA.
  useEffect(() => {
    if (!plsPickerCatalogue || typeof plsPickerCatalogue.getCompetitions !== 'function') {
      return undefined;
    }
    let cancelled = false;
    plsPickerCatalogue.getCompetitions()
      .then((comps) => {
        if (cancelled || !Array.isArray(comps)) return;
        setPlsCompetitions(comps);
        setPlsCompetitionIdRaw((prev) => {
          if (prev && comps.some((c) => c.id === prev)) return prev;
          const next = comps[0]?.id || null;
          try { if (next) localStorage.setItem('dashboard:plsCompetitionId', next); } catch { /* ignore */ }
          return next;
        });
      })
      .catch(() => { if (!cancelled) setPlsCompetitions([]); });
    return () => { cancelled = true; };
  }, [plsPickerCatalogue]);

  // OPUS-1045: discover seasons for the selected PLS competition. Prefer an
  // active season ("yes"), else the first; keep a still-valid persisted pick.
  useEffect(() => {
    if (!plsPickerCatalogue || typeof plsPickerCatalogue.getSeasons !== 'function' || !plsCompetitionId) {
      setPlsSeasons([]);
      return undefined;
    }
    let cancelled = false;
    plsPickerCatalogue.getSeasons(plsCompetitionId)
      .then((seasons) => {
        if (cancelled || !Array.isArray(seasons)) return;
        setPlsSeasons(seasons);
        setPlsSeasonIdRaw((prev) => {
          if (prev && seasons.some((s) => s.id === prev)) return prev;
          const active = seasons.find((s) => String(s.status).toLowerCase() === 'yes');
          const next = (active || seasons[0])?.id || null;
          try { if (next) localStorage.setItem('dashboard:plsSeasonId', next); } catch { /* ignore */ }
          return next;
        });
      })
      .catch(() => { if (!cancelled) setPlsSeasons([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plsPickerCatalogue, plsCompetitionId]);

  // OPUS-1125: commit the pending fixture. FIFA binds via the broker data
  // connector; PLS binds purely through the DDS dataService. Persist the
  // selection under the fixture's source BEFORE switching dataSource so the
  // source-change restore effect picks up THIS fixture (not a stale one) and
  // bumps captionReloadKey to re-resolve the form.
  const handleConnectFixture = async () => {
    const sel = pendingFixture;
    if (!sel || !sel.id) return;
    setPendingAction('connect');
    try {
      // Moving off a live FIFA broker connector onto a non-FIFA (PLS) source:
      // a PLS connect never touches the broker, so the FIFA feed would keep
      // pushing on-air. Tear it down first. (FIFA->FIFA is replace-per-renderer,
      // so the enabled:true POST below swaps it — no explicit teardown needed.)
      if (sel.source !== 'fifa' && liveConnector && liveConnector.enabled
          && liveConnector.rendererId === rendererId && liveConnector.fixtureId) {
        try {
          await fetch(`${BROKER_BASE_URL}/updateDataConnector`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled: false, fixtureId: liveConnector.fixtureId, rendererId, databaseName: liveConnector.databaseName || 'aether-db-fifa' }),
          });
        } catch (e) { console.error('FIFA connector teardown failed:', e); }
      }
      if (sel.source === 'fifa') {
        const res = await fetch(`${BROKER_BASE_URL}/updateDataConnector`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: true, fixtureId: sel.id, rendererId, databaseName: 'aether-db-fifa' }),
        });
        if (!res.ok) throw new Error('updateDataConnector failed');
      }
      if (sel.source === 'pls') {
        // OPUS-1045: pin the bind context to the browsed competition+season so
        // the DDS dataService resolves THIS fixture (and survives a reload).
        setPlsBoundCompetitionId(plsCompetitionId);
        setPlsBoundSeasonId(plsSeasonId);
        try {
          localStorage.setItem('dashboard:plsBoundCompetitionId', plsCompetitionId);
          localStorage.setItem('dashboard:plsBoundSeasonId', plsSeasonId);
        } catch { /* ignore */ }
      }
      try {
        localStorage.setItem(`dashboard:selectedFixtureId:${sel.source}`, sel.id);
        if (sel.label) localStorage.setItem(`dashboard:selectedFixtureLabel:${sel.source}`, sel.label);
        else localStorage.removeItem(`dashboard:selectedFixtureLabel:${sel.source}`);
      } catch { /* ignore */ }
      if (sel.source !== dataSource) {
        // dataSource change triggers the restore effect, which loads the
        // fixture we just persisted and bumps captionReloadKey.
        setDataSource(sel.source);
      } else {
        setSelectedFixtureId(sel.id, sel.label);
      }
      setBrokerFormData({});
      alert.success(sel.source === 'fifa' ? 'FIFA fixture connected successfully' : 'PLS fixture connected');
    } catch (e) {
      console.error('Connect fixture failed:', e);
      alert.error('Error connecting fixture data. Please try again.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleDisconnectFixture = async () => {
    const liveBrokerActive = !!(liveConnector && liveConnector.enabled
      && liveConnector.rendererId === rendererId && liveConnector.fixtureId);
    if (!selectedFixtureId && !liveBrokerActive) return;
    setPendingAction('disconnect');
    try {
      // Tear down whatever broker connector is actually live for this renderer,
      // regardless of the current source — a FIFA feed can still be live after
      // the operator switched the picker to PLS. Use the live connector's own
      // fixtureId + databaseName, not the current selection.
      if (liveBrokerActive) {
        await fetch(`${BROKER_BASE_URL}/updateDataConnector`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: false, fixtureId: liveConnector.fixtureId, rendererId, databaseName: liveConnector.databaseName || 'aether-db-fifa' }),
        });
      }
      setSelectedFixtureId(null);
      setPendingFixture(null);
    } catch (e) {
      console.error('Disconnect fixture failed:', e);
      alert.error('Error disconnecting fixture data.');
    } finally {
      setPendingAction(null);
    }
  };

  // FIFA connection is broker-truth (liveConnector); PLS is "connected" once a
  // fixture is bound to the dataService. A live FIFA broker connector also
  // counts as connected even after the picker moved to PLS, so the operator can
  // always see (and Stop) a feed that's still on-air.
  const fixtureIsConnected = (dataSource === 'fifa' && !!selectedFixtureId)
    ? !!(liveConnector && liveConnector.enabled && liveConnector.rendererId === rendererId && liveConnector.fixtureId === selectedFixtureId)
    : (!!selectedFixtureId || !!(liveConnector && liveConnector.enabled && liveConnector.rendererId === rendererId && liveConnector.fixtureId));

  const handleResetConfirmation = () => setResetConfirmOpen(true);

  const handleRendererReset = async () => {
    try {
      setIsResetting(true);
      setResetConfirmOpen(false);
      const response = await resetRenderer({
        variables: { rendererId: rendererId, reset: true }
      });
      const resetResponse = response.data.resetRenderer;
      if (!resetResponse || !resetResponse.rendererId) {
        throw new Error("Failed to reset renderer");
      }
      alert.success(`Renderer ${rendererDetails?.renderer_name || rendererId} reset successfully`);
      initialCommand.refetch();
      setCommandsLoadedFor(null);
      setBrokerFormData({});
    } catch (error) {
      console.error("Error resetting renderer:", error);
      alert.error(`Failed to reset renderer: ${error.message}`);
    } finally {
      setIsResetting(false);
    }
  };

  const handleCaptionResetConfirmation = () => setCaptionResetConfirmOpen(true);

  const handleCaptionReset = async () => {
    try {
      setIsCaptionResetting(true);
      setCaptionResetConfirmOpen(false);
      const response = await fetch(`${BROKER_BASE_URL}/resetCaptionData`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ renderer: rendererId })
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to reset caption data');
      }
      alert.success(`Caption data cleared for ${rendererDetails?.renderer_name || rendererId}`);
      initialCommand.refetch();
      setCommandsLoadedFor(null);
      lastCommand.refetch();
      setPayload({});
      setBrokerFormData({});
      setCaptionAction({});
      setClockData([]);
      setGpiDataFromQuery(null);
    } catch (error) {
      console.error('Error resetting caption data:', error);
      alert.error(`Failed to reset caption data: ${error.message}`);
    } finally {
      setIsCaptionResetting(false);
    }
  };

  // OPUS-1124: "Clear all GFX" — server-side broker mutation that animates every
  // on-air caption OUT then resets the renderer's state (single source of truth).
  const handleClearAllGfxConfirmation = () => { if (blockIfLocked()) return; setClearGfxConfirmOpen(true); };
  const handleClearAllGfx = async () => {
    try {
      setIsClearingGfx(true);
      setClearGfxConfirmOpen(false);
      const response = await fetch(`${BROKER_BASE_URL}/clearAllGfx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ renderer: rendererId })
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to clear all GFX');
      }
      alert.success(`Cleared ${data.animated ?? 0} on-air graphic(s) for ${rendererDetails?.renderer_name || rendererId}`);
      initialCommand.refetch();
      setCommandsLoadedFor(null);
      lastCommand.refetch();
      setPayload({});
      setBrokerFormData({});
      setCaptionAction({});
      setClockData([]);
      setGpiDataFromQuery(null);
    } catch (error) {
      console.error('Error clearing all GFX:', error);
      alert.error(`Failed to clear all GFX: ${error.message}`);
    } finally {
      setIsClearingGfx(false);
    }
  };

  async function checkPluginsFileExists(captionId, version, scenePath) {
    const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
    const pluginUrl = `${baseAeroUrl}${scenePath}/${captionId}/${version}/plugins.js`;
    try {
      const response = await fetch(pluginUrl);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  async function checkScenePluginExists(scenePath) {
    const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
    const pluginUrl = `${baseAeroUrl}${scenePath}/assets/scripts/scene-plugin.js`;
    try {
      const response = await fetch(pluginUrl);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  useEffect(() => {
    const loadRendererAndScene = async () => {
      try {
        const response = await apiService.getRendererDetails(rendererId, {
          attachBothSceneCaption: true
        });

        // Response shape from getRendererDetails:
        // {
        //   renderer_id, renderer_name, renderer_type, renderer_status, connectivity_status,
        //   allocation_details: { status, scope, owner, scene: { id, name, path, version, captions } },
        //   metadata: { created_at, updated_at, created_by, last_modified_by },
        //   technical_specs: { ... }
        // }

        const rendererData = response?.data || response;

        if (!rendererData) {
          console.error("Renderer not found:", rendererId);
          return;
        }

        // OPUS-913: bail out before touching any scene/caption state if this
        // renderer is locked by someone else — direct-URL access must never
        // let scene data reach the page (previously it loaded fully, then the
        // modal only covered it visually, briefly flashing real content).
        const ownerLockData = rendererData.owner_lock;
        const isSuperAdminUser = userInfo?.data?.role?.name === "super_admin";
        const lockedOutOnLoad = !!(
          ownerLockData?.locked &&
          String(ownerLockData.locked_by_user_id || "") !== String(currentUserId || "") &&
          !isSuperAdminUser
        );
        if (lockedOutOnLoad) {
          setRendererDetails({
            renderer_id: rendererData.renderer_id,
            renderer_name: rendererData.renderer_name,
            owner_lock: ownerLockData,
          });
          return;
        }

        // Extract scene from allocation_details
        const sceneFromAllocation = rendererData.allocation_details?.scene;

        // Build the merged object the page needs
        const merged = {
          // Renderer fields
          renderer_id: rendererData.renderer_id,
          renderer_name: rendererData.renderer_name,
          renderer_type: rendererData.renderer_type,
          renderer_status: rendererData.renderer_status,
          connectivity_status: rendererData.connectivity_status,
          allocation_status: rendererData.allocation_details?.status,
          allocation_scope: rendererData.allocation_details?.scope,
          // OPUS-912: preview linkage — present only when this program renderer
          // has a preview renderer ("previews enabled").
          dependent_renderer_id: rendererData.dependent_renderer_id || null,
          scene_id: sceneFromAllocation?.id || null,
          // OPUS-913: owner edit-lock, needed so direct-URL access to a locked
          // renderer is blocked (see lockedByOther below), not just list clicks.
          owner_lock: rendererData.owner_lock || null,

          // Flat scene fields (used by the header display)
          scene_name: sceneFromAllocation?.name || null,
          scene_path: sceneFromAllocation?.path || null,

          // Nested sceneData (used by plugin/asset loading checks)
          sceneData: sceneFromAllocation
            ? {
              id: sceneFromAllocation.id,
              name: sceneFromAllocation.name,
              scene_path: sceneFromAllocation.path,
              load_part_path: sceneFromAllocation.load_part_path || null,
              captions: sceneFromAllocation.captions || [],
            }
            : null,
        };


        setRendererDetails(merged);

        // Fallback: set scenePath from renderer details if broker hasn't provided it yet
        if (sceneFromAllocation?.path) {
          setScenePath(prevPath => prevPath || sceneFromAllocation.path);
        }

        // Build caption list from scene captions
        if (sceneFromAllocation?.captions?.length > 0) {
          const newCaptionList = sceneFromAllocation.captions.map((caption) => ({
            title: caption.id || caption.name, // API returns 'id' field for caption id
            version: caption.version,
            // OPUS-1391: which player layer this template renders into. Comes
            // from renderer-details; normalised so a renderer service deployed
            // ahead of scene-service still groups sensibly.
            layer: normalizeLayer(caption.layer),
            actions: ["IN", "OUT", "clear"],
          }));
          setCaptionList(newCaptionList);
          setSelectedCaption(0);

          // Eagerly detect v1/v2 for all captions in parallel so the UI
          // knows which form to render immediately when a caption is
          // clicked. We also cache the fetched JSON into `payload`
          // right here — that way V1 captions (where the click-time
          // loadMaestroForm might re-fetch and fail) still have data
          // available the moment the operator clicks them.
          const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
          const validationPath = sceneFromAllocation.path;
          Promise.all(
            newCaptionList.map(async (cap) => {
              try {
                const url = `${baseAeroUrl}${validationPath}/${cap.title}/${cap.version}/${cap.title}.json`;
                const res = await fetch(url);
                if (!res.ok) return { id: cap.title, isV2: false, json: null };
                const json = await res.json();
                return { id: cap.title, isV2: !!(json && typeof json === 'object' && json.schema), json };
              } catch {
                return { id: cap.title, isV2: false, json: null };
              }
            })
          ).then(results => {
            const statusMap = {};
            const payloadPatch = {};
            results.forEach(({ id, isV2, json }) => {
              statusMap[id] = isV2;
              if (json) payloadPatch[id] = json;
            });
            setCaptionV2Status(statusMap);
            // Merge, never replace — loadMaestroForm may have
            // already populated entries between detection start and
            // resolution.
            if (Object.keys(payloadPatch).length > 0) {
              setPayload((prev) => ({ ...payloadPatch, ...prev }));
            }
          });
        } else {
          console.warn("No captions found for this renderer's scene");
          setCaptionList([]);
        }

      } catch (error) {
        console.error("Error loading renderer details:", error);
        // Handle case where scene doesn't exist or renderer not found
        if (error.message.includes('404')) {
          alert.error("Renderer or linked scene not found");
        }
      }
    };

    loadRendererAndScene();
  }, [rendererId]);

  useEffect(() => {
    const checkScenePluginFile = async () => {
      if (rendererDetails && rendererDetails.sceneData?.scene_path) {
        const exists = await checkScenePluginExists(rendererDetails.sceneData.scene_path);
        setScenePluginExists(exists);
        if (exists) {
          try {
            const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
            const pluginUrl = `${baseAeroUrl}${rendererDetails.sceneData.scene_path}/assets/scripts/scene-plugin.js`;
            const response = await fetch(pluginUrl);
            const pluginCode = await response.text();
            const sectionTitleMatch = pluginCode.match(/const\s+sectionTitle\s*=\s*["']([^"']+)["']/);
            if (sectionTitleMatch) setPluginTitle(sectionTitleMatch[1]);
          } catch (error) {
            console.error('Error fetching plugin title:', error);
          }
        }
      }
    };
    checkScenePluginFile();
  }, [rendererDetails]);

  const [updateRenderer, { data_new, loading, error }] = useMutation(UPDATE_RENDERER);
  const [resetRenderer, { data: resetData, loading: resetLoading, error: resetError }] = useMutation(RESET_RENDERER);

  // Form-library dataService:
  //   - getContext / subscribe / getState come from useFixtureContext
  //     (fixtureDataService) so JSONata resolves against real DDS state.
  //   - evaluate stays inline — it handles inline JS functions some
  //     legacy caption definitions still embed, which is orthogonal
  //     to fixture context.
  const dataService = React.useMemo(() => ({
    getContext: () => fixtureDataService.getContext(),
    subscribe: (listener) => fixtureDataService.subscribe(listener),
    getState: () => fixtureDataService.getState && fixtureDataService.getState(),
    evaluate: async (expr, context, options) => {
      try {
        if (typeof expr === 'function') return expr(context?.data || context, options);
        if (typeof expr === 'string' && expr.includes('=>')) {
          const fn = new Function('data', 'options', `return ${expr}`);
          return fn(context?.data || context, options);
        }
        return expr;
      } catch (e) { return expr; }
    },
  }), [fixtureDataService]);

  // brokerService is identity-stable across captionAction changes —
  // getState() reads the latest snapshot through a ref so the form
  // library always sees the renderer's current per-caption action
  // state (IN / PREVIEW / clear / …) without forcing a Provider
  // reflow. MaestroActionButtons consumes this to enable/disable
  // buttons: graphic must be IN (or PREVIEW for the "IN" confirm
  // button) before OUT / BAKE / etc. unlock.
  const captionActionRef = useRef(captionAction);
  useEffect(() => { captionActionRef.current = captionAction; }, [captionAction]);

  // OPUS-913: owner edit-lock — when this renderer is locked by someone else
  // (and the viewer isn't a super_admin), Scene Controls is read-only: command
  // dispatch is blocked. Kept in a ref so the memoised brokerService sees it.
  const isSuperAdmin = userInfo?.data?.role?.name === "super_admin";
  const ownerLock = rendererDetails?.owner_lock;
  const lockedByOther = !!(ownerLock?.locked && String(ownerLock.locked_by_user_id || "") !== String(currentUserId || "") && !isSuperAdmin);
  const lockedRef = useRef(lockedByOther);
  useEffect(() => { lockedRef.current = lockedByOther; }, [lockedByOther]);
  const blockIfLocked = () => {
    if (lockedRef.current) { alert.error(`Renderer is locked by ${ownerLock?.locked_by_name || "its owner"} — read-only.`); return true; }
    return false;
  };

  // OPUS-1045: create a preview renderer for this program renderer when it has
  // none. Editor-gated server-side (RENDERERS_UPDATE); blocked here while the
  // renderer is locked by someone else. On success, optimistically set the
  // dependent_renderer_id so the preview card renders.
  const [isCreatingPreview, setIsCreatingPreview] = useState(false);
  const handleCreatePreview = async () => {
    if (blockIfLocked()) return;
    setIsCreatingPreview(true);
    try {
      const resp = await apiService.createPreviewRenderer(rendererId);
      const depId = resp?.data?.dependent_renderer_id || resp?.dependent_renderer_id;
      if (depId) setRendererDetails((prev) => ({ ...(prev || {}), dependent_renderer_id: depId }));
      alert.success("Preview renderer created");
    } catch (e) {
      alert.error(e?.message || "Failed to create preview renderer");
    } finally {
      setIsCreatingPreview(false);
    }
  };

  const brokerService = React.useMemo(() => ({
    send: async (cmd) => {
      if (lockedRef.current) { alert.error("Renderer is locked — read-only."); return; }
      if (cmd) {
        const brokerPayload = {
          // Honour the form-lib's per-command renderer: PREVIEW-mode commands
          // carry the retargeted preview renderer id; everything else falls back
          // to the program renderer (OPUS scene-controls preview).
          renderer: cmd.renderer || renderer,
          layer: cmd.caption.id,
          caption: {
            id: cmd.caption.id,
            action: cmd.caption.action,
            ...(cmd.caption.mode ? { mode: cmd.caption.mode } : {}),
            data: cmd.caption.data,
          },
        };
        await updateRenderer({ variables: { payload: brokerPayload } });
      }
    },
    // Returns the latest captionAction map (read through ref) shaped
    // exactly how ActionButton.js:152 expects: { [graphicId]: actionStr }.
    // captionAction is already keyed by caption title which equals
    // the graphic id for V2 captions.
    getState: () => captionActionRef.current || {},
  }), [renderer, updateRenderer]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = scrollRef.current?.scrollTop || 0;
      setScrolled(scrollTop > 0);
    };
    const scrollElement = scrollRef.current;
    if (scrollElement) scrollElement.addEventListener('scroll', handleScroll);
    return () => {
      if (scrollElement) scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const lastCommand = useQuery(GET_COMMANDS, {
    variables: { renderer },
    fetchPolicy: "network-only",
    skip: commandsLoadedFor === renderer,
  });
  const initialCommand = useQuery(GET_INITIAL_COMMANDS, {
    variables: { renderer },
    fetchPolicy: "cache-first",
    skip: !!initialCommadData.data,
  });
  const commandSubscription = useSubscription(COMMAND_SUBSCRIPTION, { variables: { renderer } });
  const initialSubscription = useSubscription(INITIAL_SUBSCRIPTION, { variables: { renderer } });

  useEffect(() => {
    if (initialSubscription.data?.subscriptionToInitialCommand) {
      const newInitialData = initialSubscription.data.subscriptionToInitialCommand;
      setInitialCommadData(initialCommadData => ({ ...initialCommadData, ...newInitialData }));
    }
  }, [initialSubscription]);

  useEffect(() => {
    if (initialCommand.data?.getInitialCommands) {
      const newInitialData = initialCommand.data.getInitialCommands;
      // Only adopt scenePath when the broker has a real value. On fresh
      // page loads getInitialCommands often returns scenePath:null (no
      // captions have triggered yet), and an unconditional overwrite
      // would wipe out the good value loadRendererAndScene already set
      // — leaving the loadMaestroForm effect's `if (scenePath)` guard
      // permanently false, so subsequent template clicks silently
      // never load.
      if (newInitialData.scenePath) setScenePath(newInitialData.scenePath);
      setInitialCommadData(initialCommadData => ({ ...initialCommadData, ...newInitialData }));
    }
  }, [initialCommand]);

  // Broker pushes a connector update on every /updateDataConnector POST
  // (data-connector.service.js pubSub.publish('COMMAND', { DataConnector }).
  // Drive the "Connected" pill from this so the UI shows broker truth, not
  // an optimistic local flag.
  useEffect(() => {
    const dc = commandSubscription.data?.subscriptionToCommand?.DataConnector;
    if (dc && dc.rendererId === renderer) setLiveConnector(dc);
  }, [commandSubscription, renderer]);

  useEffect(() => {
    if (commandSubscription.data?.subscriptionToCommand) {
      if (commandSubscription.data.subscriptionToCommand.caption.data) {
        setCaptionData(captionData => ({
          ...captionData,
          ...commandSubscription.data.subscriptionToCommand,
          ...{ updatedBy: 'subscription' }
        }));
        if (commandSubscription.data.subscriptionToCommand.GPI) {
          setGpiDataFromQuery(commandSubscription.data.subscriptionToCommand.GPI);
        }
        updateCaptionPayload([commandSubscription.data.subscriptionToCommand], 'subscription');
      }
    }
  }, [commandSubscription]);

  const handleSubmit = async (action, captionId) => {
    if (blockIfLocked()) return;
    const captionObject = { action: action, id: captionId };
    if (dataMode) {
      captionObject.data = { [captionId]: payload[captionId] };
    }
    const updatedPayload = {
      [captionId]: {
        renderer: renderer,
        layer: captionId,
        caption: captionObject
      }
    };
    setCaptionAction((prevData) => ({ ...prevData, [captionId]: action }));
    try {
      await updateRenderer({ variables: { payload: updatedPayload[captionId] } });
    } catch (err) {
      console.error("Error", err);
    }
  };

  useEffect(() => {
    if (commandsLoadedFor === renderer) return;
    if (lastCommand.data && captionList.length > 0) {
      const commands = lastCommand.data?.getCommands;
      setCaptionData((prevData) => ({ ...prevData, ...commands[0], ...{ updatedBy: "query" } }));
      // getCommands' DataConnector omits rendererId (broker builds it by
      // hand in caption.service.js — only the live subscription path
      // includes it). Stamp the renderer we queried for so the
      // isLiveConnected rendererId-match holds on the refresh path and
      // the "Connected" pill reflects the still-live broker connector.
      if (commands[0]?.DataConnector) setLiveConnector({ ...commands[0].DataConnector, rendererId: renderer });
      if (commands[0]?.GPI) setGpiDataFromQuery(commands[0].GPI);
      if (!commands || commands.length === 0) return;
      updateCaptionPayload(commands, 'query');
      setCommandsLoadedFor(renderer);
    } else if (!lastCommand.loading) {
      setTimeout(() => { if (lastCommand.refetch) lastCommand.refetch(); }, 1000);
    }
  }, [lastCommand, captionList, commandsLoadedFor, renderer]);

  useEffect(() => {
    setRenderer(rendererId);
    setPayload({});
    setBrokerFormData({});
    setCommandsLoadedFor(null);
    setCaptionData({ renderer: rendererId, caption: { action: "clear", id: "" }, updatedBy: "none" });
    setGpiDataFromQuery(null);
    setClockData([]);
    setCaptionAction({});
    setSelectedCaption(null);
    setRendererDetails(null);
    setCaptionsWithPlugins({});
  }, [rendererId]);

  useEffect(() => {
    if (captionData?.caption?.data?.VizGlobal) {
      const newCaptionData = captionData.caption.data.VizGlobal.Clocks;
      const newClockData = [];
      for (const key in newCaptionData) {
        if (Object.prototype.hasOwnProperty.call(newCaptionData, key)) {
          const clock = newCaptionData[key];
          newClockData.push({
            clockName: key,
            label: `Clock ${key.replace('Clock', '')}`,
            action: clock.strClockStatus,
            actions: ['START', 'STOP', 'CONTINUE'],
            data: {
              iClockTime: clock.iClockTime || 0,
              iClockLimit: clock.iClockLimit || 0,
              strClockDirection: clock.strClockDirection || 'UP'
            }
          });
        }
      }
      setClockData(prev => {
        const updatedClocks = prev.map(existingClock => {
          const newClock = newClockData.find(c => c.clockName === existingClock.clockName);
          if (newClock) return { ...existingClock, action: newClock.action, label: newClock.label, actions: newClock.actions };
          return existingClock;
        });
        const newClocks = newClockData.filter(c => !prev.find(e => e.clockName === c.clockName));
        return [...updatedClocks, ...newClocks];
      });
    }
  }, [captionData]);

  const updateCaptionPayload = (commands, updatedBy) => {
    let updatedPayload = {};
    const updatedCaptionAction = {};
    const captions = {};
    captionList.forEach((caption) => { captions[caption.title] = true; });
    for (let i = 0; i < commands.length; i++) {
      const command = commands[i];
      if (captions[command.caption.id]) {
        updatedPayload = command.caption.data;
        updatedCaptionAction[command.caption.id] = command.caption.action;
      }
    }
    // Preserve any caption-definition fields (schema, v_ui, v_actions, …)
    // that loadMaestroForm / the eager v1-v2 detect already loaded into
    // payload[captionId]. The broker's getCommands result carries the
    // operational caption data ({ Main: {…}, brokerRefDate, … }) keyed
    // by captionId; a top-level spread would replace the whole entry and
    // strip the schema, leaving the form stuck on "awaiting def" after
    // any operator-edited refresh.
    updatedBy === 'query' && setPayload((prevData) => {
      const next = { ...prevData };
      for (const k of Object.keys(updatedPayload)) {
        next[k] = { ...next[k], ...updatedPayload[k] };
      }
      return next;
    });
    setBrokerFormData((prevData) => {
      const next = { ...prevData };
      for (const k of Object.keys(updatedPayload)) {
        next[k] = updatedPayload[k];
      }
      return next;
    });
    setCaptionAction((prevData) => ({ ...prevData, ...updatedCaptionAction }));
  };

  function groupByBaseLabel(input) {
    const groups = {};
    const defaults = [];
    input.forEach(item => {
      const parts = item.split('_');
      if (parts.length > 1) {
        const state = parts.pop();
        const base = parts.join('_');
        if (!groups[base]) groups[base] = [];
        groups[base].push(state);
      } else {
        defaults.push(item);
      }
    });
    const result = Object.entries(groups).map(([key, value]) => ({ [key]: value }));
    if (defaults.length) result.unshift({ DEFAULT: defaults });
    return result;
  }

  useEffect(() => {
    if (captionList.length > 0 && selectedCaption !== null) {
      const captionId = captionList[selectedCaption]?.title;
      if (scenePath) loadMaestroForm(captionId);
    }
  }, [captionList, selectedCaption, scenePath]);

  // Force the form-lib's persisted uiSettings into the shape this page
  // needs. MaestroProvider layers defaults ⊕ defaultUiSettings ⊕
  // persisted (localStorage), with persisted winning — so if the
  // operator ever toggled showForm off via F-key (or ignoreHides off
  // via the cog) in another consumer, the value persists across all of
  // them. SceneControls' CAPTION PROPERTIES accordion only makes sense
  // with the form visible AND with ignoreHides on so every property
  // surfaces, so we patch both stored values on mount.
  useEffect(() => {
    try {
      const KEY = "maestro-ui-settings";
      const raw = localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      // Dashboard's baseline Maestro UI state, re-asserted every mount.
      // The maestro-ui-settings localStorage key is SHARED across all
      // form-lib consumers, so another app (AeDrive, MOS) could have
      // persisted values that don't suit Dashboard — we patch them
      // back on each load so Dashboard always opens consistently.
      const DASHBOARD_DEFAULTS = {
        debugMode: false,
        ignoreHides: true,
        showForm: true,
        hideDataSourceFields: true,
        hideCaptionDrivenFields: false,
      };
      const next = { ...parsed, ...DASHBOARD_DEFAULTS };
      const changed = Object.keys(DASHBOARD_DEFAULTS).some(
        (k) => parsed[k] !== DASHBOARD_DEFAULTS[k],
      );
      if (changed) localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* ignore — localStorage unavailable; cog toggles + F-key still work */
    }
  }, []);

  // V2 captions only — derive the full JSON definition from the
  // payload cache that loadMaestroForm already populates. Single
  // source of truth: avoids the race we had with a parallel fetch.
  // Resets to null when selection moves to a non-V2 caption or its
  // JSON hasn't been fetched yet.
  const captionDefinition = React.useMemo(() => {
    if (selectedCaption === null) return null;
    const caption = captionList[selectedCaption];
    if (!caption?.title) return null;
    if (captionV2Status[caption.title] !== true) return null;
    const def = payload[caption.title];
    if (!def || !def.schema) return null;
    const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
    const url = `${baseAeroUrl}${scenePath}/${caption.title}/${caption.version}/${caption.title}.json`;
    return { id: caption.title, name: caption.title, path: url, ...def };
  }, [selectedCaption, captionList, captionV2Status, payload, scenePath]);

 
  const [selectedCaptionKeyState, setSelectedCaptionKeyState] = useState(null);
  const selectedCaptionKeyIdRef = React.useRef(undefined);
  const captionKeyStorageId = captionDefinition?.id ? `${renderer}:${captionDefinition.id}` : null;
  let selectedCaptionKey = selectedCaptionKeyState;
  if (selectedCaptionKeyIdRef.current !== captionKeyStorageId) {
    selectedCaptionKeyIdRef.current = captionKeyStorageId;
    selectedCaptionKey = null;
    if (captionKeyStorageId) {
      try {
        selectedCaptionKey = localStorage.getItem(`dashboard:selectedCaptionKey:${captionKeyStorageId}`) || null;
      } catch { selectedCaptionKey = null; }
    }
    setSelectedCaptionKeyState(selectedCaptionKey);
  }

  const setSelectedCaptionKey = React.useCallback((key) => {
    if (!captionDefinition?.id) return;
    setSelectedCaptionKeyState(key);
    try {
      const storageKey = `dashboard:selectedCaptionKey:${renderer}:${captionDefinition.id}`;
      if (key) localStorage.setItem(storageKey, key);
      else localStorage.removeItem(storageKey);
    } catch { /* ignore */ }
  }, [renderer, captionDefinition?.id]);

  async function loadMaestroForm(captionId) {
    const captionVersion = captionList.find((caption) => caption.title === captionId)?.version;
    const absoluteScenePath = scenePath;
    const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
    const maestroUrl = `${baseAeroUrl}${absoluteScenePath}/${captionId}/${captionVersion}/${captionId}.json`;
    const actionsUrl = `${baseAeroUrl}${absoluteScenePath}/${captionId}/${captionVersion}/Actions.txt`;

    // ── CRITICAL PATH ──────────────────────────────────────────────
    // Fetch + set the V2 schema state FIRST, before any ancillary
    // requests. Previously the plugin-file check ran ahead of this and
    // a 404 (common; many captions don't have plugins) threw before
    // setPayload ever ran — captionV2Status was already partially set
    // by a prior call but payload[captionId] stayed empty, locking the
    // UI to "awaiting def".
    let result = null;
    try {
      const response = await fetch(maestroUrl);
      result = await response.json();
      const isV2 = result && typeof result === 'object' && result.schema ? true : false;
      const isFirstLoad = !payload[captionId];
      setPayload((prevData) => ({ ...prevData, [captionId]: result }));
      setCaptionV2Status(prev => ({ ...prev, [captionId]: isV2 }));
      if (isFirstLoad) {
        setCaptionData({ renderer, caption: { action: "clear", id: "" }, updatedBy: "none" });
      }
      setSharedState(result);
    } catch (error) {
      console.error(`[loadMaestroForm] failed to load definition for ${captionId}:`, error);
      return; // can't continue without the definition
    }

    // ── ANCILLARY (plugin metadata + actions list) ─────────────────
    // Each in its own try/catch — failures here MUST NOT taint the V2
    // critical path above. The form library doesn't depend on either.
    try {
      const hasPlugins = await checkPluginsFileExists(captionId, captionVersion, absoluteScenePath);
      setCaptionsWithPlugins(prev => ({ ...prev, [captionId]: hasPlugins }));
    } catch (error) {
      console.warn(`[loadMaestroForm] plugin check failed for ${captionId}:`, error);
      setCaptionsWithPlugins(prev => ({ ...prev, [captionId]: false }));
    }

    try {
      const actionsListResponse = await fetch(actionsUrl);
      if (actionsListResponse.ok) {
        const actionList = await actionsListResponse.text();
        const actionListTrimed = actionList.trim().split('\n').map(item => item.trim());
        setActionList(Object.values(groupByBaseLabel(actionListTrimed)));
      } else {
        setActionList([]);
      }
    } catch (error) {
      console.warn(`[loadMaestroForm] actions list fetch failed for ${captionId}:`, error);
      setActionList([]);
    }
  }

  useEffect(() => {
    if (rendererDetails && rendererDetails.sceneData?.scene_path) {
      try {
        const scenePathStr = rendererDetails.sceneData.scene_path;
        const parts = scenePathStr.split('/').filter(Boolean);
        if (parts.length >= 3) {
          setOrganizationName(parts[1] || '');
          setSceneName(parts[2] || '');
        }
      } catch (e) {
        console.warn('Failed to parse organization/scene from scene_path');
      }
    }
  }, [rendererDetails]);

  useEffect(() => {
    const loadConfigAndAssets = async () => {
      if (!rendererDetails?.sceneData?.scene_path || !organizationName || !sceneName) return;
      try {
        const config = await readAssetPluginConfig(rendererDetails.sceneData.scene_path);
        if (config) {
          setPluginConfig(config);
          const basePath = config.allowedPath || config.baseAssetPath;
          if (basePath) {
            const cleanBasePath = basePath.replace(/^\/+/, '').replace(/\/+$/, '');
            const prefix = `scenes/${organizationName}/${sceneName}/assets/${cleanBasePath}/`;
            const response = await apiService.listS3Objects({ prefix, organizationName, sceneName, delimiter: '/' });
            if (response?.success) {
              const assets = [];
              if (response.data.files && response.data.files.length > 0) {
                response.data.files.forEach(file => {
                  const fileType = file.fileType || (file.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|svg|webp|avif)$/) ? 'image' : 'document');
                  if (fileType === 'image') assets.push(file.path);
                });
              }
              if (config.allowedPath && config.allowedPath !== basePath) {
                try {
                  const cleanAllowed = config.allowedPath.replace(/^\/+/, '').replace(/\/+$/, '');
                  const prefixAllowed = `scenes/${organizationName}/${sceneName}/assets/${cleanAllowed}/`;
                  const respAllowed = await apiService.listS3Objects({ prefix: prefixAllowed, organizationName, sceneName, delimiter: '/' });
                  if (respAllowed?.success && respAllowed.data?.files?.length) {
                    respAllowed.data.files.forEach(file => {
                      const fileType = file.fileType || (file.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|svg|webp|avif)$/) ? 'image' : 'document');
                      if (fileType === 'image') assets.push(file.path);
                    });
                  }
                } catch (_) { }
              }
              const relOptions = assets.map(p => {
                const parts = p.split('/');
                const idx = parts.indexOf('assets');
                return idx >= 0 ? parts.slice(idx + 1).join('/') : p;
              });
              setAssetOptions(Array.from(new Set(relOptions)).filter(Boolean));
            } else {
              setAssetOptions([]);
            }
          }
        }
      } catch (e) {
        console.warn('Failed to load plugin config/assets', e);
      }
    };
    loadConfigAndAssets();
  }, [rendererDetails, organizationName, sceneName]);

  async function readAssetPluginConfig(scenePath) {
    const maxRetries = 2;
    const retryDelay = 500;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
        if (!baseAeroUrl) throw new Error('Base Aero URL not configured');
        if (!scenePath) throw new Error('Scene path not provided');
        const pluginUrl = `${baseAeroUrl}${scenePath}/assets/scripts/asset-plugin.js`;
        const response = await fetch(pluginUrl);
        if (!response.ok) throw new Error(`Plugin not found: ${response.status} ${response.statusText}`);
        const pluginCode = await response.text();
        const baseAssetPathMatch = pluginCode.match(/const\s+baseAssetPath\s*=\s*['"`]([^'"`]+)['"`]/);
        const allowedPathMatch = pluginCode.match(/const\s+allowedPath\s*=\s*['"`]([^'"`]+)['"`]/);
        const objectStructureMatch = pluginCode.match(/const\s+objectStructure\s*=\s*(\{[\s\S]*?\});/);
        const captionIdMatch = pluginCode.match(/const\s+captionId\s*=\s*['"`]([^'"`]+)['"`]/);
        let objectStructure = null;
        if (objectStructureMatch) {
          try { objectStructure = eval(`(${objectStructureMatch[1]})`); } catch (_) { }
        }
        const config = {
          baseAssetPath: baseAssetPathMatch ? baseAssetPathMatch[1] : null,
          allowedPath: allowedPathMatch ? allowedPathMatch[1] : null,
          objectStructure,
          captionId: captionIdMatch ? captionIdMatch[1] : null
        };
        if (!config.baseAssetPath || !config.allowedPath || !config.objectStructure || !config.captionId) {
          try {
            const safeEval = new Function('pluginCode', `
              const module = {}; const exports = {}; const require = () => {}; const console = { log: () => {} };
              eval(pluginCode);
              const pluginInstance = plugins();
              return {
                baseAssetPath: pluginInstance.getBaseAssetPath ? pluginInstance.getBaseAssetPath() : null,
                allowedPath: pluginInstance.getAllowedPath ? pluginInstance.getAllowedPath() : null,
                objectStructure: pluginInstance.getObjectStructure ? pluginInstance.getObjectStructure() : null,
                captionId: pluginInstance.getCaptionId ? pluginInstance.getCaptionId() : null
              };
            `);
            const functionConfig = safeEval(pluginCode);
            config.baseAssetPath = config.baseAssetPath || functionConfig.baseAssetPath;
            config.allowedPath = config.allowedPath || functionConfig.allowedPath;
            config.objectStructure = config.objectStructure || functionConfig.objectStructure;
            config.captionId = config.captionId || functionConfig.captionId;
          } catch (_) { }
        }
        if (!config.baseAssetPath || !config.allowedPath || !config.objectStructure || !config.captionId) {
          throw new Error('Incomplete plugin configuration');
        }
        return config;
      } catch (err) {
        if (attempt === maxRetries) throw err;
        await new Promise(r => setTimeout(r, retryDelay));
      }
    }
  }

  const handleUpdateClick = () => {
    if (formRef.current) formRef.current.submitForm();
  };

  const startResize = (index, e) => {
    e.preventDefault();
    const container = containerRef.current;
    const startX = e.clientX;
    const containerWidth = container.offsetWidth;
    const startWidths = [...widths];
    setIsDragging(index);
    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaFraction = deltaX / containerWidth;
      let newWidths = [...startWidths];
      // Two-pane resize: pick the delta that respects BOTH the moving
      // pane's MIN/MAX and its neighbour's MIN/MAX, then apply it as
      // +delta to one and -delta to the other. The third pane stays
      // exactly fixed. Renormalizing here was the bug: when a clamp
      // engaged the total drifted off 1, and dividing through shrank
      // every pane proportionally — including the one we tried to
      // pin.
      if (index === 0) {
        const maxGrow = Math.min(MAX_WIDTHS[0] - startWidths[0], startWidths[1] - MIN_WIDTHS[1]);
        const maxShrink = Math.min(startWidths[0] - MIN_WIDTHS[0], MAX_WIDTHS[1] - startWidths[1]);
        const allowedDelta = Math.max(Math.min(deltaFraction, maxGrow), -maxShrink);
        newWidths[0] = startWidths[0] + allowedDelta;
        newWidths[1] = startWidths[1] - allowedDelta;
        newWidths[2] = startWidths[2];
      } else if (index === 1) {
        const maxGrow = Math.min(MAX_WIDTHS[1] - startWidths[1], startWidths[2] - MIN_WIDTHS[2]);
        const maxShrink = Math.min(startWidths[1] - MIN_WIDTHS[1], MAX_WIDTHS[2] - startWidths[2]);
        const allowedDelta = Math.max(Math.min(deltaFraction, maxGrow), -maxShrink);
        newWidths[0] = startWidths[0];
        newWidths[1] = startWidths[1] + allowedDelta;
        newWidths[2] = startWidths[2] - allowedDelta;
      }
      setWidths(newWidths);
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setIsDragging(null);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const updateClockField = (index, field, value) => {
    setClockData(prev => {
      const updated = [...prev];
      updated[index].data[field] = value;
      return updated;
    });
  };

  const handleClockSubmit = (currentAction, newAction, clockName) => {
    if (blockIfLocked()) return;
    const index = clockData.findIndex(clock => clock.clockName === clockName);
    if (index === -1) return;
    setClockData(prev => {
      const updated = [...prev];
      updated[index].action = newAction;
      return updated;
    });
    const clockPayload = {
      "renderer": renderer,
      "layer": "VizGlobal",
      "caption": {
        "id": "VizGlobal",
        "action": "",
        "data": { "VizGlobal": { "Clocks": {} } }
      }
    };
    if (newAction === 'START') {
      clockPayload.caption.action = `${clockName.toUpperCase()}_START`;
      clockPayload.caption.data.VizGlobal.Clocks[clockName] = {
        iClockTime: clockData[index].data.iClockTime,
        iClockLimit: clockData[index].data.iClockLimit,
        strClockDirection: clockData[index].data.strClockDirection
      };
    } else if (newAction === 'STOP') {
      clockPayload.caption.action = `${clockName.toUpperCase()}_STOP`;
      delete clockPayload.caption.data;
    } else if (newAction === 'CONTINUE') {
      clockPayload.caption.action = `${clockName.toUpperCase()}_CONTINUE`;
      delete clockPayload.caption.data;
    }
    updateRenderer({ variables: { payload: clockPayload } });
  };

  const addNewClock = () => {
    const nextIndex = clockData.length;
    const newClockName = `Clock${nextIndex}`;
    setClockData(prev => [...prev, {
      clockName: newClockName,
      label: `Clock ${nextIndex}`,
      action: 'START',
      actions: ['START', 'STOP', 'CONTINUE'],
      data: { [newClockName]: { iClockTime: "0", iClockLimit: "2700", strClockDirection: "UP" } }
    }]);
  };

  const handleAssetUpdate = async (updateData) => {
    try {
      const { objectStructure, captionId } = updateData;
      const mutationPayload = {
        renderer: renderer,
        layer: captionId,
        caption: { action: 'update', id: captionId, data: { [captionId]: objectStructure } }
      };
      const result = await updateRenderer({ variables: { payload: mutationPayload } });
      alert.success('Asset updated successfully');
      return result;
    } catch (error) {
      alert.error(`Failed to update asset: ${error.message}`);
      throw error;
    }
  };

  // ─── JSX: identical to original ───────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 16px)', overflow: 'hidden' }}>
      {/* OPUS-1148: align the breadcrumb header to the canonical inset (flush to
          the dashboard outlet's 8px padding) so its baseline matches other pages —
          was px/pt 0.5 (+4px), which read as a different breadcrumb position. */}
      <Box sx={{ flexShrink: 0, px: 0, pt: 0, pb: 1 }}>
        <DashboardBreadcrumbs
          trail={[{ label: 'Scene controls', to: '/dashboard/renderer-management' }, { label: rendererDetails?.renderer_name || rendererId }]}
          rightAction={viewers.length > 0 ? <PresenceStack viewers={viewers} size={28} /> : null}
        />
      </Box>

      {/* OPUS-913: direct-URL access (typed link, bookmark, or a copied renderer
          URL) skips the list's click-time lock check entirely — this modal
          re-enforces the lock at the page itself, blocking view of scene
          controls/data for anyone who isn't the lock owner or a super_admin.
          Uses the shared OpusModal (same blurred-scrim treatment as the user
          guide / other app modals) instead of a plain MUI Dialog. No onClose —
          this is a hard block, not dismissible via X/Esc/backdrop; the only
          way out is "Back to renderer list". */}
      <OpusModal
        open={lockedByOther}
        centered
        icon="lock"
        title="Renderer locked"
        subtitle={`Locked by ${ownerLock?.locked_by_name || "a supervisor"}`}
        footer={
          <OpusButton variant="primary" onClick={() => navigate('/dashboard/renderer-management')}>
            Back to renderer list
          </OpusButton>
        }
      >
        <Typography sx={{ fontSize: 13.5, color: 'var(--text-muted)' }}>
          You can't open Scene Controls while this renderer is locked.
        </Typography>
      </OpusModal>

    <Box ref={containerRef} container direction={'row'} spacing={0} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', width: '100%', overflow: 'hidden', position: 'relative', ...(lockedByOther ? { pointerEvents: 'none', userSelect: 'none' } : {}) }}>

      <Box sx={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)', height: '100%', borderRadius: 'var(--r-lg)', flexBasis: `${widths[0] * 100}%`, flexGrow: 0, flexShrink: 0, minWidth: "23%", overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* OPUS-1045: the breadcrumb already carries the drawer toggle (same
            toggleDrawer/isDrawerOpen), so the duplicate arrow here was removed
            — TEMPLATES header is now just the label. */}
        <Box sx={{ display: 'flex', color: 'var(--text-muted)', justifyContent: 'flex-start', px: 1.25, height: '3em', alignItems: 'center', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>TEMPLATES</Typography>
        </Box>

        <Box className="opus-scroll" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {captionLayerGroups.groups.map((group) => (
          <Box key={group.value}>
            {captionLayerGroups.showHeaders && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, pt: 1, pb: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '2px', background: group.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-dim)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {group.label}
                </Typography>
              </Box>
            )}
        <List sx={{ m: 0, p: 0.2, px: .5 }}>
          {group.rows.map(({ caption, index }) => (
            <ListItem sx={{ m: 0, p: 0, color: selectedCaption === index ? 'var(--green)' : 'var(--text)' }} key={caption.title || index}
              secondaryAction={
                <Box sx={{ position: 'absolute', right: -12, top: 0, transform: 'translateY(-50%)' }}>
                  <ToggleButtonGroup sx={{ m: 0, p: 0, gap: 0.5, backgroundColor: 'transparent', '& .MuiToggleButtonGroup-grouped': { border: '1px solid var(--border)', borderRadius: '5px !important', mx: 0 } }} value={captionAction[caption?.title]} exclusive size="small">
                    {caption.actions.map((action, index) => (
                      <ToggleButtonMUI color={action.toUpperCase() === 'OUT' ? 'error' : action.toUpperCase() === 'IN' ? 'seafoamGreen' : ''} key={index} sx={{ color: 'var(--text-muted)', p: 0, pl: 0.75, pr: 0.75, fontWeight: 600, fontSize: '0.65rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.02em', lineHeight: 1.6, '&:hover': { backgroundColor: 'var(--surface-3)' } }} value={action} onClick={() => handleSubmit(action, caption?.title)}>{action.toUpperCase()}</ToggleButtonMUI>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              }
            >
              <ListItemButton sx={{ m: 0, p: .1, pl: 1, pt: .75, pb: .75, borderRadius: 0, borderLeft: selectedCaption === index ? '3px solid var(--green)' : '3px solid transparent', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 0.5, '&.Mui-selected': { backgroundColor: 'rgba(69,255,167,0.06)' }, '&.Mui-selected:hover': { backgroundColor: 'rgba(69,255,167,0.1)' }, '&:hover': { backgroundColor: 'var(--surface-2)' } }} selected={selectedCaption === index} onClick={() => {
                if (selectedCaption === index) {
                  // Re-clicking the active template forces a MaestroProvider
                  // remount so caption data re-resolves against the current
                  // fixture / data source. Necessary when this scene only has
                  // one template (no other row to bounce off of).
                  setCaptionReloadKey((k) => k + 1);
                } else {
                  setSelectedCaption(index);
                }
              }}>
                {captionV2Status[caption.title] !== undefined && (
                  <Chip label={captionV2Status[caption.title] ? 'v2' : 'v1'} size="small" sx={{ flexShrink: 0, height: 16, fontSize: '0.6rem', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-dim)', backgroundColor: 'var(--surface-3)', borderRadius: '4px', '& .MuiChip-label': { px: 0.5 } }} />
                )}
                <ListItemText slotProps={{ primary: { sx: { color: selectedCaption === index ? 'var(--green)' : 'var(--text)', fontFamily: 'var(--font-mono)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } }}>{caption.title}</ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
          </Box>
        ))}
        </Box>

        {/* OPUS-1148: plugin controls panel disabled — it reserved a large empty
            gap (mt:50 ≈ 400px) in the pane and isn't in use yet. Re-enable by
            removing the `false &&` guard once plugin controls are ready. */}
        {false && rendererDetails && rendererDetails.sceneData?.scene_path && scenePluginExists && (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', borderTop: '3px solid var(--border)', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', mt: 50 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pr: 1, height: '2em', alignItems: 'center', flexShrink: 0, backgroundColor: "var(--surface-2)", borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <Button size="small" variant="contained" color="silver" sx={{ fontWeight: 'bold', p: 0, mx: 1, px: 1, visibility: 'hidden', backgroundColor: 'var(--surface-2)' }}>Placeholder</Button>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{pluginTitle}</Typography>
              </Box>
            </Box>
            <Box sx={{ backgroundColor: "var(--surface-2)", borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <PluginRenderer captionId={captionList[selectedCaption]?.title} version={captionList[selectedCaption]?.version} scenePath={rendererDetails?.sceneData?.scene_path} rendererId={rendererId} />
            </Box>
          </Box>
        )}
      </Box>

      <Box onMouseDown={(e) => startResize(0, e)} sx={{ cursor: 'col-resize', backgroundColor: (isDragging !== null && isDragging === 0) ? 'var(--border-strong)' : 'transparent', position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', m: .2, mr: .3, '&:hover': { backgroundColor: 'var(--border-strong)' }, borderRadius: '4px' }}>
        <Box sx={{ width: '4px', height: '50px', borderRadius: '8px', backgroundColor: 'transparent' }} />
      </Box>

      <Box className="opus-scroll" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)', flexBasis: `${widths[1] * 100}%`, flexGrow: 0, flexShrink: 0, borderRadius: 'var(--r-lg)' }}>
        {/* TEMP DEBUG 2026-05-14 — instant state visibility for the
            fragment-only path. Remove once stable. */}
        {(() => {
          const t = selectedCaption !== null ? captionList[selectedCaption]?.title : null;
          const isV2 = t ? captionV2Status[t] : null;
          const hasDef = !!captionDefinition;
          const tag = !t ? "no caption" : hasDef ? "def loaded" : isV2 === true ? "awaiting def" : isV2 === false ? "no schema (legacy caption)" : "checking…";
          // Does the loaded definition contain JSONata binding
          // templates ({{ }})? If so it needs a fixture context to
          // resolve — flag when one hasn't been picked yet.
          const needsBinding = hasDef && JSON.stringify(captionDefinition).includes('{{');
          const bindingUnresolved = needsBinding && !selectedFixtureId;
          return (
            <Box sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-dim)', px: 2, py: 1, borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <span>[debug]</span>
              <span style={{ color: 'var(--green)' }}>{t || '—'}</span>
              <span>· {tag}</span>
              <span>· src:{dataSource}</span>
              <span style={{ color: selectedFixtureId ? 'var(--green)' : 'var(--amber)' }}>
                · fixture:{selectedFixtureId ? `${dataSource} connected` : 'none'}
              </span>
              {needsBinding && (
                <span style={{ color: bindingUnresolved ? 'var(--coral)' : 'var(--green)', fontWeight: 'bold' }}>
                  · {bindingUnresolved ? '⚠ data-bound — no fixture, bindings unresolved' : 'data-bound ✓'}
                </span>
              )}
            </Box>
          );
        })()}

        {/* DATA SOURCE — first step: pick a fixture before driving captions. */}
        <Accordion expanded={isDataSourceOpen} onChange={() => setIsDataSourceOpen(!isDataSourceOpen)} sx={{ backgroundColor: 'transparent !important', borderRadius: 0, overflow: 'hidden', flexShrink: 0, '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid var(--border)', margin: 0, '&.Mui-expanded': { margin: '0 0 4px 0', backgroundColor: 'transparent !important' } }}>
          <AccordionSummary expandIcon={<OpusIcon name="chevronDown" size={16} style={{ color: 'var(--text-dim)' }} />} sx={{ height: 'auto', minHeight: '2.6em !important', px: 1, py: 0.75, backgroundColor: 'transparent !important', '&.Mui-expanded': { minHeight: '2.6em !important' }, '& .MuiAccordionSummary-content': { margin: 0, justifyContent: 'space-between', alignItems: 'center' }, '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 } }}>
            {/* Collapsed/expanded summary — the operator always sees which
                source + fixture is active in the accordion header. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, overflow: 'hidden' }}>
              <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: selectedFixtureId ? 'var(--green)' : 'var(--text-dim)', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>{dataSource}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-dim)', flexShrink: 0 }}>·</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: selectedFixtureId ? 'var(--text-muted)' : 'var(--text-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedFixtureLabel || (selectedFixtureId ? 'fixture connected' : 'no fixture selected')}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>DATA SOURCE</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, backgroundColor: 'transparent !important' }}>
            {/* OPUS-1125: unified FIFA + PLS picker. Pulls both sources in
                parallel; the picked fixture's source drives the bind. The
                explicit Connect button below commits the pick for BOTH
                sources (no auto-bind on select). */}
            <MergedFixturePicker
              fifaCatalogue={fifaPickerCatalogue}
              plsCatalogue={plsPickerCatalogue}
              fifaCompetitions={fifaCompetitions}
              fifaCompetitionId={fifaCompetitionId}
              onFifaCompetitionChange={setFifaCompetitionId}
              plsCompetitions={plsCompetitions}
              plsCompetitionId={plsCompetitionId}
              onPlsCompetitionChange={setPlsCompetitionId}
              plsSeasons={plsSeasons}
              plsSeasonId={plsSeasonId}
              onPlsSeasonChange={setPlsSeasonId}
              selectedFixtureId={pendingFixture?.id || selectedFixtureId}
              onSelect={(id, label, source) => setPendingFixture(id ? { id, label, source } : null)}
              size="small"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', color: fixtureIsConnected ? 'var(--green)' : 'var(--text-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {fixtureIsConnected
                  ? `Connected · ${String(dataSource).toUpperCase()}`
                  : (pendingFixture ? 'Not connected — press Connect' : 'Pick a fixture')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                {fixtureIsConnected && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={!!pendingAction}
                    sx={{ fontWeight: 'bold', p: 0.4 }}
                    onClick={handleDisconnectFixture}
                  >
                    {disconnecting ? 'Stopping…' : 'Stop'}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="seafoamGreen"
                  size="small"
                  disabled={
                    !!pendingAction
                    || !pendingFixture
                    || (fixtureIsConnected && pendingFixture?.id === selectedFixtureId && pendingFixture?.source === dataSource)
                  }
                  sx={{ fontWeight: 'bold', p: 0.4 }}
                  onClick={handleConnectFixture}
                >
                  {connecting
                    ? 'Connecting…'
                    : (fixtureIsConnected && pendingFixture?.id !== selectedFixtureId ? 'Switch & Connect' : 'Connect')}
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Unified right-pane stack — V1 fallback path removed
            2026-05-14. Form-lib fragments are now the only editor;
            captions without a schema show an empty-state placeholder
            instead of falling back to the legacy ToggleButton list +
            JsonForm. Wrapped in MaestroProvider only when a caption
            definition is loaded. */}
        {(() => {
          const ready = selectedCaption !== null && !!captionDefinition;
          // V1 caption — detected as non-V2. CAPTION DATA renders the
          // legacy JsonForm (which is its own self-contained editor +
          // state); CAPTION ACTIONS is disabled because the V1 editor
          // owns its own command path.
          //
          // Render the form even when payload[currentTitle] is empty
          // (some V1 captions are plugin-driven and have no JSON at
          // the standard URL). JsonForm with empty data renders an
          // empty form rather than nothing — at least gives the
          // operator a surface to type into.
          const currentTitle = selectedCaption !== null ? captionList[selectedCaption]?.title : null;
          const isV1 = currentTitle && captionV2Status[currentTitle] === false;
          // Per-caption schema source — beats the global sharedState
          // which carries the last-loaded caption's shape and would
          // make this V1 caption render with the wrong fields.
          const v1Data = (isV1 && payload[currentTitle]) || {};

          const captionActionsAccordion = (
            <Accordion expanded={isCaptionActionListOpen} onChange={() => toggleCaptionActionList(!isCaptionActionListOpen)} sx={{ backgroundColor: 'transparent !important', borderRadius: 0, overflow: 'hidden', flexShrink: 0, '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid var(--border)', margin: 0, '&.Mui-expanded': { margin: '0 0 4px 0', backgroundColor: 'transparent !important' } }}>
              <AccordionSummary expandIcon={<OpusIcon name="chevronDown" size={16} style={{ color: 'var(--text-dim)' }} />} sx={{ height: 'auto', minHeight: '2.6em !important', px: 1, py: 0.75, backgroundColor: 'transparent !important', '&.Mui-expanded': { minHeight: '2.6em !important' }, '& .MuiAccordionSummary-content': { margin: 0, justifyContent: 'space-between', alignItems: 'center' }, '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 } }}>
                <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                  <OpusToggle checked={dataMode} onChange={(v) => setDataMode(v)} label="Data mode" />
                  <Typography sx={{ color: '#fff', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Data Mode</Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>CAPTION ACTIONS</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1.5, backgroundColor: 'transparent !important' }}>
                {ready ? (
                  <>
                    <MaestroCaptionNavView />
                    <MaestroCaptionInputsView />
                    <MaestroActionButtonsView />
                  </>
                ) : isV1 ? (
                  // V1 caption actions — driven by the scene's
                  // Actions.txt (grouped by base label). Each row is a
                  // base action with its state buttons; clicking fires
                  // `${baseAction}_${state}` (or just `${state}` for the
                  // DEFAULT group) through the legacy handleSubmit.
                  actionList.length > 0 ? (
                    <List sx={{ m: 0, p: 0 }}>
                      {actionList.map((actionGroup, index) => {
                        const [baseAction, states] = Object.entries(actionGroup)[0];
                        return (
                          <ListItem
                            key={index}
                            sx={{ m: 0, p: 0.2, pl: 0.5, borderRadius: '4px', color: 'var(--text-muted)' }}
                            secondaryAction={
                              <ToggleButtonGroup
                                value={(() => {
                                  const current = captionAction[currentTitle];
                                  if (!current) return null;
                                  if (baseAction === "DEFAULT") return current;
                                  for (const state of states) {
                                    if (current === `${baseAction}_${state}`) return state;
                                  }
                                  return null;
                                })()}
                                exclusive
                                size="small"
                                sx={{ m: 0, p: 0 }}
                              >
                                {states.map((actionButton, idx) => {
                                  const up = actionButton.toUpperCase();
                                  return (
                                    <ToggleButtonMUI
                                      className="toggle-button"
                                      key={idx}
                                      color={up === 'OUT' ? 'error' : up === 'IN' ? 'seafoamGreen' : ''}
                                      value={actionButton}
                                      sx={{ color: 'var(--text-muted)', p: 0, px: 1, fontWeight: 'bold', fontSize: '0.875rem', '&:hover': { background: 'var(--surface-3)' }, '&.Mui-selected': { backgroundColor: up === 'OUT' ? 'rgba(211, 47, 47, 0.25)' : up === 'IN' ? 'rgba(69, 255, 167, 0.25)' : 'rgba(255, 255, 255, 0.15)', color: up === 'OUT' ? 'var(--coral)' : up === 'IN' ? 'var(--green)' : 'var(--text)', fontWeight: 'bold' } }}
                                      onClick={() => {
                                        const actionToSubmit = baseAction === "DEFAULT" ? actionButton : `${baseAction}_${actionButton}`;
                                        handleSubmit(actionToSubmit, currentTitle);
                                      }}
                                    >
                                      {up}
                                    </ToggleButtonMUI>
                                  );
                                })}
                              </ToggleButtonGroup>
                            }
                          >
                            <ListItemText
                              slotProps={{ primary: { sx: { fontSize: '0.875rem', color: '#fff' } } }}
                            >
                              {baseAction}
                            </ListItemText>
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography sx={{ color: 'var(--text-dim)', fontSize: '0.875rem', textAlign: 'center', py: 2 }}>No actions available for this caption.</Typography>
                  )
                ) : (() => {
                  if (selectedCaption === null) return <Typography sx={{ color: 'var(--text-dim)', fontSize: '0.875rem', textAlign: 'center', py: 2 }}>Select a template to load its actions.</Typography>;
                  const t = captionList[selectedCaption]?.title;
                  const v2 = t ? captionV2Status[t] : undefined;
                  if (v2 === undefined) return <Typography sx={{ color: 'var(--text-dim)', fontSize: '0.875rem', textAlign: 'center', py: 2 }}>Checking template…</Typography>;
                  return <Typography sx={{ color: 'var(--text-dim)', fontSize: '0.875rem', textAlign: 'center', py: 2 }}>Loading caption definition…</Typography>;
                })()}
              </AccordionDetails>
            </Accordion>
          );

          const clockControlAccordion = (
            <Accordion expanded={isClockControlOpen} onChange={() => toggleClockControl(!isClockControlOpen)} sx={{ backgroundColor: 'transparent !important', borderRadius: 0, overflow: 'hidden', flexShrink: 0, '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid var(--border)', margin: 0, '&.Mui-expanded': { margin: '0 0 4px 0', backgroundColor: 'transparent !important' } }}>
              <AccordionSummary expandIcon={<OpusIcon name="chevronDown" size={16} style={{ color: 'var(--text-dim)' }} />} sx={{ height: 'auto', minHeight: '2.6em !important', px: 1, py: 0.75, backgroundColor: 'transparent !important', '&.Mui-expanded': { minHeight: '2.6em !important' }, '& .MuiAccordionSummary-content': { margin: 0, justifyContent: 'space-between', alignItems: 'center' }, '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 } }}>
                <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); addNewClock(); }} color="seafoamGreen" sx={{ fontWeight: 'bold', p: 0, px: 1, visibility: isClockControlOpen ? 'visible' : 'hidden', pointerEvents: isClockControlOpen ? 'auto' : 'none' }}>Add Clock</Button>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>CLOCK CONTROL</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1.5, backgroundColor: 'transparent !important' }}>
                {clockData.length === 0 ? (
                  <Typography sx={{ color: 'var(--text-dim)', fontSize: '0.875rem', textAlign: 'center', py: 1 }}>
                    No clocks available
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {clockData.map((clock, index) => (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor: 'var(--surface-2)',
                          border: '1px solid var(--border)',
                          borderRadius: '6px',
                          p: 1.25,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.25,
                        }}
                      >
                        {/* Row 1: name + transport buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                          >
                            {clock.label || clock.clockName}
                          </Typography>
                          <ToggleButtonGroup
                            value={clock.action}
                            exclusive
                            size="small"
                            sx={{ flexShrink: 0, '& .MuiToggleButtonGroup-grouped': { border: '1px solid rgba(255, 255, 255, 0.12)' } }}
                          >
                            {clock.actions.map((actionButton, idx) => {
                              const iconName = actionButton === 'START' ? 'play' : actionButton === 'STOP' ? 'x' : 'chevronsRight';
                              const buttonColor = actionButton === 'START' ? 'seafoamGreen' : actionButton === 'STOP' ? 'error' : 'primary';
                              return (
                                <Tooltip title={actionButton} key={idx}>
                                  <ToggleButtonMUI
                                    className="toggle-button"
                                    color={buttonColor}
                                    value={actionButton}
                                    onClick={() => handleClockSubmit(clock.action, actionButton, clock.clockName)}
                                    sx={{ color: '#fff', p: 0.5, minWidth: '32px', '&:hover': { background: 'var(--surface-3)' } }}
                                  >
                                    <OpusIcon name={iconName} size={15} />
                                  </ToggleButtonMUI>
                                </Tooltip>
                              );
                            })}
                          </ToggleButtonGroup>
                        </Box>
                        {/* Row 2: equal-width start / limit / direction */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            size="small"
                            label="Start"
                            type="number"
                            variant="outlined"
                            value={clock.data.iClockTime}
                            onChange={(e) => updateClockField(index, 'iClockTime', e.target.value)}
                            InputProps={{ inputProps: { min: 0 } }}
                            sx={{ flex: 1, '& .MuiInputBase-input': { color: 'var(--text)' }, '& .MuiInputLabel-root': { color: 'var(--text-muted)' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-strong)' } }}
                          />
                          <TextField
                            size="small"
                            label="Limit"
                            type="number"
                            variant="outlined"
                            value={clock.data.iClockLimit}
                            onChange={(e) => updateClockField(index, 'iClockLimit', e.target.value)}
                            InputProps={{ inputProps: { min: 0 } }}
                            sx={{ flex: 1, '& .MuiInputBase-input': { color: 'var(--text)' }, '& .MuiInputLabel-root': { color: 'var(--text-muted)' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-strong)' } }}
                          />
                          <TextField
                            size="small"
                            select
                            label="Direction"
                            value={clock.data.strClockDirection}
                            onChange={(e) => updateClockField(index, 'strClockDirection', e.target.value)}
                            sx={{ flex: 1, '& .MuiInputBase-input': { color: 'var(--text)' }, '& .MuiSelect-icon': { color: 'var(--text-muted)' }, '& .MuiInputLabel-root': { color: 'var(--text-muted)' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-strong)' } }}
                          >
                            <MenuItem value="UP">UP</MenuItem>
                            <MenuItem value="DOWN">DOWN</MenuItem>
                          </TextField>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );

          const captionDataAccordion = (
            <Accordion expanded={isCaptionDataOpen} onChange={() => toggleCaptionData(!isCaptionDataOpen)} sx={{ backgroundColor: 'transparent !important', borderRadius: 0, overflow: 'hidden', flexShrink: 0, '&:before': { display: 'none' }, boxShadow: scrolled ? '0px 5px 5px 0px rgba(0, 0, 0, 0.3)' : 'none', transition: 'box-shadow 0.3s ease', borderBottom: '1px solid var(--border)', margin: 0, '&.Mui-expanded': { margin: 0, backgroundColor: 'transparent !important' } }}>
              <AccordionSummary expandIcon={<OpusIcon name="chevronDown" size={16} style={{ color: 'var(--text-dim)' }} />} sx={{ height: 'auto', minHeight: '2.6em !important', px: 1, py: 0.75, backgroundColor: 'transparent !important', '&.Mui-expanded': { minHeight: '2.6em !important' }, '& .MuiAccordionSummary-content': { margin: 0, justifyContent: 'space-between', alignItems: 'center' }, '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 } }}>
                {/* Update button — V1-only. V2 form submits live via
                    onFormDataChange so it doesn't need an explicit
                    save action. */}
                {isV1 ? (
                  <Button
                    type="submit"
                    form="rjsf-form"
                    variant="outlined"
                    color="seafoamGreen"
                    size="small"
                    sx={{ fontWeight: 'bold', p: 0, px: 1, visibility: isCaptionDataOpen ? 'visible' : 'hidden', pointerEvents: isCaptionDataOpen ? 'auto' : 'none' }}
                    onClick={(e) => { e.stopPropagation(); handleUpdateClick(); }}
                  >
                    Update
                  </Button>
                ) : <Box />}
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>CAPTION DATA</Typography>
              </AccordionSummary>
              <AccordionDetails ref={scrollRef} sx={{ p: 1.5, maxHeight: 'calc(100vh - 22em)', overflow: 'auto', backgroundColor: 'transparent !important', "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(188, 20, 20, 0.5)" }, '& > *': { backgroundColor: 'transparent !important' } }}>
                {ready ? (
                  // Scoped override of the form-lib's input styling to
                  // the Dashboard dark palette. The perf form injects a
                  // global `.maestro-form-root` stylesheet that gives
                  // inputs a #0d1117 navy fill + #4FC3F7 blue focus
                  // border — the "blue boxes". Their selectors are
                  // (0,2,1) specificity, so we both scope to
                  // `.maestro-form-root` AND use !important to win.
                  <Box
                    sx={{
                      '& .maestro-form-root input, & .maestro-form-root select, & .maestro-form-root textarea': {
                        backgroundColor: 'var(--surface-2) !important',
                        color: '#fff !important',
                        borderColor: 'var(--border) !important',
                        borderRadius: '6px !important',
                      },
                      '& .maestro-form-root input:hover, & .maestro-form-root select:hover, & .maestro-form-root textarea:hover': {
                        borderColor: 'var(--border-strong) !important',
                      },
                      '& .maestro-form-root input:focus, & .maestro-form-root select:focus, & .maestro-form-root textarea:focus': {
                        borderColor: '#45FFA7 !important',
                        boxShadow: '0 0 0 1px rgba(69, 255, 167, 0.3) !important',
                      },
                      '& .maestro-form-root input[type="checkbox"], & .maestro-form-root input[type="radio"]': {
                        accentColor: '#45FFA7 !important',
                      },
                      '& .maestro-form-root option': {
                        backgroundColor: '#1e1e1e',
                        color: '#fff',
                      },
                    }}
                  >
                    <MaestroFormView />
                  </Box>
                ) : isV1 ? (
                  // V1 editor — legacy JsonForm with its own action
                  // surface. No `key={captionId}`; switching V1 captions
                  // updates JsonForm's props in place so the form
                  // reconciles instead of snap-remounting.
                  //
                  // sharedState is sourced from this caption's own
                  // payload (falling back to {} when the JSON 404s) so
                  // the form's generated schema reflects the current
                  // caption — not whichever caption happened to load
                  // last into the global sharedState.
                  <JsonForm
                    ref={formRef}
                    sharedState={v1Data}
                    payload={payload}
                    setPayload={setPayload}
                    captionId={currentTitle}
                    pluginConfig={pluginConfig}
                    assetOptions={assetOptions}
                    organizationName={organizationName}
                    sceneName={sceneName}
                    scenePath={rendererDetails?.sceneData?.scene_path}
                  />
                ) : (() => {
                  if (selectedCaption === null) return <Typography sx={{ color: 'var(--text-dim)', fontSize: '0.875rem', textAlign: 'center', py: 2 }}>Select a template to edit its data.</Typography>;
                  const t = captionList[selectedCaption]?.title;
                  const v2 = t ? captionV2Status[t] : undefined;
                  if (v2 === undefined) return <Typography sx={{ color: 'var(--text-dim)', fontSize: '0.875rem', textAlign: 'center', py: 2 }}>Checking template…</Typography>;
                  return <Typography sx={{ color: 'var(--text-dim)', fontSize: '0.875rem', textAlign: 'center', py: 2 }}>Loading caption definition…</Typography>;
                })()}
              </AccordionDetails>
            </Accordion>
          );

          const stack = (
            <>
              {/* MaestroHeaderView surfaces the form-lib settings cog
                  (showForm / ignoreHides / debug border toggles etc.)
                  Wrapped in its own container with a seafoam accent
                  stripe so it reads as a primary header rather than
                  another fragment sunken between the accordions. */}
              {ready && (
                <Box sx={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '6px',
                  borderLeft: '3px solid var(--amber)',
                  border: '1px solid var(--border)',
                  borderLeftWidth: '3px',
                  borderLeftColor: 'var(--amber)',
                  m: 2,
                  px: 1.75,
                  py: 1,
                  flexShrink: 0,
                }}>
                  <MaestroHeaderView />
                </Box>
              )}
              {/* MaestroJsonViewerView — operator toggles visibility
                  via the cog's "Show JSON Viewer" switch
                  (uiSettings.showJsonViewer). No wrapper, so when off
                  it renders nothing and the layout is clean; when on
                  it takes the full middle-column width. */}
              {ready && <MaestroJsonViewerView />}
              {captionActionsAccordion}
              {/* OPUS-1148: GPIControls now renders its own accordion (matching the
                  other sections) and returns null when there's no real GPI data, so
                  no empty wrapper box is left behind. */}
              {selectedCaption !== null && captionList[selectedCaption] && (
                <GPIControls captionId={captionList[selectedCaption].title} version={captionList[selectedCaption].version} scenePath={scenePath} gpiDataFromQuery={gpiDataFromQuery} rendererId={rendererId} />
              )}
              {clockControlAccordion}
              {captionDataAccordion}
            </>
          );

          if (ready) {
            const rawSeed = brokerFormData[captionDefinition.id] || null;
            let seededFormData = null;
            if (rawSeed && captionDefinition.schema?.properties) {
              const picked = {};
              for (const k of Object.keys(captionDefinition.schema.properties)) {
                if (rawSeed[k] !== undefined) picked[k] = rawSeed[k];
              }
              if (Object.keys(picked).length > 0) seededFormData = picked;
            }
            const hydrationMarker = seededFormData ? 'h' : 'e';
            return (
              <MaestroProvider
                key={`${captionDefinition.id}-${captionReloadKey}-${hydrationMarker}`}
                initialFormData={seededFormData}
                initialCaption={selectedCaptionKey || undefined}
                isReconstruction={!!seededFormData}
                open={true}
                /* mount="modal" (the default) keeps the form-lib's
                   settings cog visible in MaestroHeaderView. The
                   modal-wrapping logic lives inside ComposeChrome,
                   which we don't mount here -- fragments render
                   directly into the accordions, so mount=modal only
                   ends up affecting the cog visibility gate. */
                mount="modal"
                graphicId={captionDefinition.id}
                graphicPath={captionDefinition.path}
                graphicName={captionDefinition.name}
                schema={captionDefinition.schema}
                uiSchema={captionDefinition.v_ui}
                captionsSchema={captionDefinition.v_captions}
                actionsSchema={captionDefinition.v_actions}
                customActions={captionDefinition.customActions}
                controllersConfig={captionDefinition.v_config}
                rendererId={renderer}
                dataService={dataService}
                brokerService={brokerService}
                bindData={bindData}
                onFormDataChange={(payload) => {
                  if (payload?.caption) setSelectedCaptionKey(payload.caption);
                }}
                sendDefaultOnOpen={false}
                /* OPUS-912: previews. When this program renderer has a preview
                   renderer, retarget PREVIEW-mode commands at it (same env) and
                   seed the embedded preview pane. Uses the stored
                   dependent_renderer_id directly — no suffix derivation. */
                directorPreviewRendererId={rendererDetails?.dependent_renderer_id || undefined}
                previewRendererUrl={rendererDetails?.dependent_renderer_id
                  ? `https://${process.env.REACT_APP_BROKER_URL}/?renderer=${rendererDetails.dependent_renderer_id}`
                  : undefined}
                /* showPreview:false — the preview renderer now renders in the
                   right-column Preview card (OPUS-913 reskin). Director-preview
                   command retargeting still works via directorPreviewRendererId
                   + brokerService, independent of this display gate. */
                defaultUiSettings={{ debugMode: false, ignoreHides: true, showForm: true, hideDataSourceFields: true, hideCaptionDrivenFields: false, showPreview: false }}
              >
                {stack}
                {rendererDetails?.dependent_renderer_id ? <MaestroPreviewView /> : null}
              </MaestroProvider>
            );
          }
          return stack;
        })()}
      </Box>

      <Box onMouseDown={(e) => startResize(1, e)} sx={{ cursor: 'col-resize', backgroundColor: (isDragging !== null && isDragging === 1) ? 'var(--border-strong)' : 'transparent', position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', m: .2, mr: .3, '&:hover': { backgroundColor: 'var(--border-strong)' }, borderRadius: '4px' }}>
        <Box sx={{ width: '4px', height: '50px', borderRadius: '2px', backgroundColor: 'transparent' }} />
      </Box>

      <Box className="opus-scroll" sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, flexBasis: `${widths[2] * 100}%`, flexGrow: 0, overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}>

        {/* PROGRAM card */}
        <Box sx={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', p: 1.75, flexShrink: 0 }}>
          {/* OPUS-1045: the renderer name on top of the gfx is redundant (the
              PROGRAM badge + breadcrumb + URL already identify it) and was
              confusing — show only the loaded scene name + the actions. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, minWidth: 0 }}>
            {rendererDetails?.scene_name && (
              <Typography sx={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1, minWidth: 0 }}>
                {rendererDetails?.scene_name}
              </Typography>
            )}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25, alignItems: 'center', flexShrink: 0 }}>
              {(() => {
                const rendererUrl = `https://${process.env.REACT_APP_BROKER_URL}/?renderer=${rendererId}`;
                return (
                  <>
                    <Tooltip title="Open renderer in a new tab" arrow>
                      <IconButton size="small" onClick={() => window.open(rendererUrl, '_blank', 'noopener,noreferrer')} sx={{ color: 'var(--text-dim)', '&:hover': { color: 'var(--green)', backgroundColor: 'rgba(69,255,167,0.08)' } }}>
                        <OpusIcon name="external" size={15} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={rendererUrlCopied ? 'Copied!' : 'Copy renderer URL'} arrow>
                      <IconButton size="small" onClick={() => { navigator.clipboard.writeText(rendererUrl); setRendererUrlCopied(true); setTimeout(() => setRendererUrlCopied(false), 1500); }} sx={{ color: rendererUrlCopied ? 'var(--green)' : 'var(--text-dim)', '&:hover': { color: 'var(--green)', backgroundColor: 'rgba(69,255,167,0.08)' } }}>
                        <OpusIcon name={rendererUrlCopied ? 'check' : 'copy'} size={15} />
                      </IconButton>
                    </Tooltip>
                    {/* OPUS-1124 replaced Reset with "Clear all GFX", but the two do
                        different jobs: Clear all GFX animates on-air graphics out,
                        while Reset (RESET_RENDERER) makes the aero-controller reload
                        its iframe — the only way to recover a renderer that is wedged
                        rather than merely showing something. Restored alongside.
                        Warning rather than error styling: it disrupts every connected
                        client, but it is a recovery action, not a destructive one, and
                        two red buttons side by side read as equally final. */}
                    <Button variant="outlined" color="warning" size="small" startIcon={<OpusIcon name="refresh" size={13} />} sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 'var(--r-sm)', p: 0.3, pl: 1, pr: 1, ml: 0.5 }} onClick={handleResetConfirmation} disabled={isResetting}>{isResetting ? 'Resetting…' : 'Reset'}</Button>
                    <Button variant="outlined" color="error" size="small" startIcon={<OpusIcon name="power" size={13} />} sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 'var(--r-sm)', p: 0.3, pl: 1, pr: 1, ml: 0.5 }} onClick={handleClearAllGfxConfirmation} disabled={isClearingGfx}>{isClearingGfx ? 'Clearing…' : 'Clear all GFX'}</Button>
                  </>
                );
              })()}
            </Box>
          </Box>
          <Box sx={{ position: 'relative' }}>
            <SceneViewer sx={{ width: '100%' }} name={'PROGRAM'} url={process.env.REACT_APP_BROKER_URL} rendererId={rendererId} preview={false} debug={false} hideFooter />
            <Box sx={{ position: 'absolute', left: 10, bottom: 10 }}><OpusBadge tone="neutral" mono>PROGRAM</OpusBadge></Box>
          </Box>
          <Box sx={{ mt: 1.5, px: 1.25, py: 0.75, backgroundColor: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ flex: 1, fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {`https://${process.env.REACT_APP_BROKER_URL}/?renderer=${rendererId}`}
            </Typography>
            <Tooltip title={rendererUrlCopied ? 'Copied!' : 'Copy URL'} arrow>
              <IconButton size="small" onClick={() => { navigator.clipboard.writeText(`https://${process.env.REACT_APP_BROKER_URL}/?renderer=${rendererId}`); setRendererUrlCopied(true); setTimeout(() => setRendererUrlCopied(false), 1500); }} sx={{ color: 'var(--text-dim)', '&:hover': { color: 'var(--green)' } }}>
                <OpusIcon name="copy" size={14} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Preview renderer card — shows the assigned preview renderer's off-air
            output (read-only). Director-preview command retargeting is unchanged. */}
        <Box sx={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', p: 1.75, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ color: 'var(--green)', display: 'flex' }}><OpusIcon name="eye" size={16} /></Box>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>Preview renderer</Typography>
            <Box sx={{ ml: 'auto' }}>
              {rendererDetails?.dependent_renderer_id
                ? <OpusBadge tone="green" dot>Live</OpusBadge>
                : <OpusBadge tone="amber" icon="lock">Not assigned</OpusBadge>}
            </Box>
          </Box>
          {rendererDetails?.dependent_renderer_id ? (
            <>
              <Box sx={{ position: 'relative' }}>
                <SceneViewer sx={{ width: '100%' }} name={'PREVIEW'} url={process.env.REACT_APP_BROKER_URL} rendererId={rendererDetails.dependent_renderer_id} preview={true} debug={false} hideFooter />
                <Box sx={{ position: 'absolute', left: 10, bottom: 10 }}><OpusBadge tone="green" dot mono>PREVIEW · LIVE</OpusBadge></Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                {[['Preview ID', rendererDetails.dependent_renderer_id], ['Scene', rendererDetails?.scene_name], ['Env', process.env.REACT_APP_ENV || 'PRD']].map(([k, v]) => (
                  <Box key={k} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{k}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'var(--text)', fontFamily: 'var(--font-mono)', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{v || '—'}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Box sx={{ border: '1px dashed var(--border-strong)', borderRadius: 'var(--r-md)' }}>
              {/* OPUS-1045: offer to create a preview renderer when none exists. */}
              <StateShell dense icon="monitor" iconTone="amber" title="No preview renderer"
                message="This renderer has no dedicated preview renderer assigned, so graphics can't be reviewed off-air before going to program."
                action={
                  <OpusButton variant="primary" size="sm" icon="plus" onClick={handleCreatePreview} disabled={isCreatingPreview || lockedByOther}>
                    {isCreatingPreview ? 'Creating…' : 'Create preview renderer'}
                  </OpusButton>
                } />
            </Box>
          )}
        </Box>

        {/* Caption data card */}
        <Box sx={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', p: 1.75, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff' }}>Caption data</Typography>
          <Button variant="outlined" color="error" size="small" startIcon={<OpusIcon name="refresh" size={13} />} sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 'var(--r-sm)' }} onClick={handleCaptionResetConfirmation} disabled={isCaptionResetting}>Reset Caption Data</Button>
        </Box>

        {/* Confirmation dialogs — MUI portals, position-independent */}
        <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)} PaperProps={{ style: { backgroundColor: 'var(--elevated)', border: '1px solid var(--border-strong)', borderRadius: '8px', color: 'var(--text)' } }} sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>{"Reset Renderer?"}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <DialogContentText sx={{ color: 'var(--text-muted)' }}>Are you sure you want to reset this renderer? There could be other clients connected to it, and all will be reset.</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setResetConfirmOpen(false)} variant="outlined" sx={{ fontWeight: 'bold', p: 0.5, px: 1.5 }}>Cancel</Button>
            <Button onClick={handleRendererReset} variant="contained" color="error" autoFocus sx={{ fontWeight: 'bold', p: 0.5, px: 1.5 }}>Yes I'm sure</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={captionResetConfirmOpen} onClose={() => setCaptionResetConfirmOpen(false)} PaperProps={{ style: { backgroundColor: 'var(--elevated)', border: '1px solid var(--border-strong)', borderRadius: '8px', color: 'var(--text)' } }} sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Reset Caption Data?</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <DialogContentText sx={{ color: 'var(--text-muted)' }}>This will delete all caption state, caption data, GPI state, and initial caption records for this renderer. This action cannot be undone.</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCaptionResetConfirmOpen(false)} variant="outlined" sx={{ fontWeight: 'bold', p: 0.5, px: 1.5 }}>Cancel</Button>
            <Button onClick={handleCaptionReset} variant="contained" color="error" autoFocus sx={{ fontWeight: 'bold', p: 0.5, px: 1.5 }}>Yes, Clear All</Button>
          </DialogActions>
        </Dialog>

        {/* OPUS-1124: Clear all GFX confirmation */}
        <Dialog open={clearGfxConfirmOpen} onClose={() => setClearGfxConfirmOpen(false)} PaperProps={{ style: { backgroundColor: 'var(--elevated)', border: '1px solid var(--border-strong)', borderRadius: '8px', color: 'var(--text)' } }} sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Clear all GFX?</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <DialogContentText sx={{ color: 'var(--text-muted)' }}>This animates every on-air graphic OUT, then resets this renderer's caption state. Use it to take everything off air at once.</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setClearGfxConfirmOpen(false)} variant="outlined" sx={{ fontWeight: 'bold', p: 0.5, px: 1.5 }}>Cancel</Button>
            <Button onClick={handleClearAllGfx} variant="contained" color="error" autoFocus sx={{ fontWeight: 'bold', p: 0.5, px: 1.5 }}>Clear all GFX</Button>
          </DialogActions>
        </Dialog>
      </Box>

    </Box>
    </Box>
  );
};

export default SceneControls;
